// NuVe Boost — full feature component.
//
// Direct port of the v1.0 mockup (nuve-boost-v1.jsx) with three changes:
//   1. Hard-coded CUST / INIT_HIST / INIT_TPL replaced by Redux state
//      sourced from /boost/* endpoints.
//   2. send / recharge / template CRUD / admin save / cancel scheduled
//      now dispatch sagas instead of mutating local arrays.
//   3. Phone-frame chrome (notch, 390px width) only renders on mobile.
//      Desktop (>= 900px) drops the chrome and lets the content fill a
//      max-width=460 column, preserving every screen's interior design.
//
// Spec compliance is preserved end-to-end: greeting types, STOP footer,
// unicode-emoji 160→70 limit, photo limits (4 × 5MB), multi-source
// audience builder with dedup, calendar/time picker, credit states,
// markup math (fixed | %), Alpha vs Global, platform-owner admin gate.

import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import BoostRechargeStripe from './BoostRechargeStripe';
import TopToolbar from '../global/subcomponents/topToolbar/TopToolbar';
import FeedbackBar from '../feedbackBar/FeedbackBar';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SettingsIcon from '@material-ui/icons/Settings';
import BarChartIcon from '@material-ui/icons/BarChart';
import FileDownloadIcon from '@material-ui/icons/CloudDownload';
import withShipment from '../../withShipment';
import { userInfoSelector } from '../../redux/selectors/auth';
import {
  fetchBoostHistory,
  fetchBoostCustomers,
  fetchBoostTemplates,
  fetchBoostCredit,
  fetchBoostSettings,
  fetchBoostAnalytics,
  fetchBoostPlatformConfig,
  fetchBoostDetail,
  sendBoostBulk,
  retryBoostMessage,
  retryBoostRecipient,
  cancelBoostMessage,
  saveBoostTemplate,
  deleteBoostTemplate,
  saveBoostSettings,
  saveBoostAdmin,
  resetBoostBalance,
  pauseBoost,
} from '../../redux/actions/boost';
import { friendlyTwilioError } from './twilioErrors';

// ── THEME ──
// Light-mode palette. The page is white with neutral light backgrounds
// for sub-surfaces; the only dark element is the hero gradient banner
// on the Home screen header (kept for visual identity).
//
// `bg` is the page background. `panel` is the light gray neutral used
// for inset/non-card surfaces (tab bars, accordion bodies, etc.).
// `softBorder`/`softText` are the light replacements for the formerly
// rgba(255,255,255,0.X) tokens — anywhere the old palette painted white
// text on dark, we now paint dark text on a light neutral.
const C = {
  bg: '#ffffff',
  panel: '#f5f6fa',
  panelDim: '#eef0f6',
  white: '#fff',
  border: '#e2e5f1',
  borderLight: '#eef0f6',
  accent: '#5b4cdb',
  accentLight: '#7c6fef',
  accentPale: '#ede9ff',
  green: '#0fa968',
  greenPale: '#e6f9ef',
  greenGlow: 'rgba(15,169,104,0.12)',
  orange: '#e8870a',
  orangePale: '#fff5e0',
  red: '#e5443a',
  redPale: '#fff0f0',
  purple: '#8b5cf6',
  purplePale: '#ede9fe',
  text: '#1a1d2e',
  textSec: '#4a4f6a',
  textMuted: '#8b90a8',
  textDim: '#b0b5c9',
  // Soft replacements for what used to be rgba(255,255,255,X) on dark
  softText: '#4a4f6a',
  softTextMuted: '#787c95',
  softTextDim: '#9aa0b7',
  softBg: '#f5f6fa',
  softBgAlt: '#e8e9f0',
  softBgRow: '#eef0f6',
  softBorder: '#e2e5f1',
  hStart: '#4338ca',
  hMid: '#6d5edb',
  hEnd: '#a78bfa',
  inputBg: '#f0f1f7',
};
const STOP_FOOTER = 'Reply STOP to unsubscribe';
const MAX_MB = 5;
// Display fallbacks ONLY for the brief moment between mount and the
// platformConfig fetch resolving. Real values come from
// state.boost.platformConfig (rates, chip options, defaults).
const FALLBACK_PHONE_FEE = 0;
const FALLBACK_SMS_BASE = 0.012;
const FALLBACK_MMS_BASE = 0.032;
const grad = `linear-gradient(135deg,${C.hStart},${C.accentLight})`;
const gradG = `linear-gradient(135deg,${C.green},#059669)`;
const dis = '#e2e5f1';
const disTxt = '#b0b5c9';

// Downscale + re-encode a picked photo to a JPEG data URL before it goes
// into the send payload. Phones shoot 5-10MB images; Twilio caps MMS
// media at 5MB total and carriers transcode anything over ~600KB anyway,
// so ~1280px JPEG keeps quality while keeping the JSON payload (and the
// MMS) small. GIFs skip compression (canvas would freeze the animation);
// anything the browser can't decode falls back to the original data URL —
// the backend re-validates size/type regardless.
const COMPRESS_MAX_W = 1280;
const COMPRESS_QUALITY = 0.8;
const compressPhoto = (dataUrl, mimeType) =>
  new Promise((resolve) => {
    if (mimeType === 'image/gif') {
      resolve(dataUrl);
      return;
    }
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, COMPRESS_MAX_W / (img.width || 1));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        // White matte under transparent PNGs — JPEG has no alpha.
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', COMPRESS_QUALITY));
      } catch (e) {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

const isValidPhone = (p) => (p || '').replace(/\D/g, '').length >= 10;
// GSM 03.38 basic set - 1 septet each. Deliberately includes LF and CR
// (line breaks are legal GSM-7 and must NOT trip the UCS-2 70-char limit)
// and the accented letters Spanish messages actually use.
const GSM7_BASIC = new Set(
  '@£$¥èéùìòÇ\nØø\r' +
    'ÅåΔ_ΦΓΛΩΠΨΣΘ' +
    'ΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
    '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§' +
    '¿abcdefghijklmnopqrstuvwxyzäöñüà'
);
// GSM 03.38 extension set - sent as ESC + char, so each costs 2 septets.
const GSM7_EXT = new Set('^{}\\[~]|€');
// Characters that genuinely force UCS-2: emoji, curly quotes, en/em dashes.
const unicodeChars = (t) => {
  const bad = [];
  for (const ch of t || '') {
    if (!GSM7_BASIC.has(ch) && !GSM7_EXT.has(ch) && !bad.includes(ch)) {
      bad.push(ch);
    }
  }
  return bad;
};
// Billable units for a string under an already-decided encoding.
// UCS-2 bills per UTF-16 code unit; GSM-7 bills per septet (ext chars = 2).
const encodedLen = (t, uni) => {
  let n = 0;
  for (const ch of t || '') {
    n += uni ? ch.length : GSM7_EXT.has(ch) ? 2 : 1;
  }
  return n;
};
const smsInfo = (t) => {
  const l = (t || '').length;
  if (!l) return { c: 0, s: 0, uni: false, lim: 160, bad: [] };
  const bad = unicodeChars(t);
  const uni = bad.length > 0;
  const lim = uni ? 70 : 160;
  const segSize = uni ? 67 : 153;
  const c = encodedLen(t, uni);
  return { c, s: c <= lim ? 1 : Math.ceil(c / segSize), uni, lim, bad };
};
// Mirrors BoostMessageService.assembleBody() — the string Twilio actually
// receives is location line + greeting + body + STOP footer, NOT just the
// textarea. Counting only the textarea under-reported segments, so a
// "1 segment" estimate could be billed as 2. {{FIRST_NAME}} is stamped per
// recipient at dispatch; we size it with the server's own fallback,
// 'Customer'.
const NAME_SAMPLE = 'Customer';
const assembleSms = (locationName, greeting, greetingText, body, footer) => {
  let s = '';
  const loc = (locationName || '').trim();
  if (loc) s += `${loc}\n`;
  if (greeting === 'generic') {
    s += `${greetingText || 'Hello!'}\n`;
  } else if (greeting === 'name') {
    s += `${(greetingText || 'Hello').replace(/!$/, '')} ${NAME_SAMPLE}!\n`;
  }
  s += body || '';
  if (footer) s += `\n${STOP_FOOTER}`;
  return s;
};
const fmtT = (d) => {
  let h = d.getHours();
  const m = d.getMinutes();
  const a = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m < 10 ? '0' : ''}${m} ${a}`;
};
const ms = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const fmtDate = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return `${ms[d.getMonth()]} ${d.getDate()}, ${fmtT(d)}`;
  } catch (e) {
    return '';
  }
};

// ── ICONS ──
const I = ({ d, size = 20, color = C.textMuted }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {d}
  </svg>
);
const ic = {
  send: (
    <>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  check: <polyline points="20 6 9 17 4 12" />,
  chevR: <polyline points="9 18 15 12 9 6" />,
  chevL: <polyline points="15 18 9 12 15 6" />,
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  x: (
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>
  ),
  mega: (
    <>
      <path d="M3 11l18-5v12L3 13v-2z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </>
  ),
  cal: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </>
  ),
  upload: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  edit: (
    <>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </>
  ),
  alert: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </>
  ),
  copy: (
    <>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </>
  ),
  retry: (
    <>
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </>
  ),
  trash: (
    <>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </>
  ),
  zap: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
  bookmark: <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />,
  dollar: (
    <>
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </>
  ),
  bar: (
    <>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </>
  ),
};
const Star = ({ size = 20, color = C.orange }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    stroke={color}
    strokeWidth="1.5"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// ── RESPONSIVE SHELL ──
// Mobile: 390x844 phone frame with notch + status bar (original mockup
// chrome). Desktop (>= 900px): drop the frame, center a 460px column,
// add subtle outer padding. The internal screen layouts don't change.
function useIsDesktop() {
  const [v, setV] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(min-width: 900px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 900px)');
    const handler = (e) => setV(e.matches);
    mq.addEventListener
      ? mq.addEventListener('change', handler)
      : mq.addListener(handler);
    return () =>
      mq.removeEventListener
        ? mq.removeEventListener('change', handler)
        : mq.removeListener(handler);
  }, []);
  return v;
}

/**
 * Standard app shell — TopToolbar + FeedbackBar at the top, full-width
 * white page below. Matches the layout used by EmployeeSession, Note,
 * Customer, etc. so NuVe Boost feels like part of the same app instead
 * of an embedded mobile preview.
 *
 *   - Page background: white
 *   - Content fills the available width (capped at 1400px on huge
 *     screens just so line lengths stay readable)
 *   - Optional back button injected as a TopToolbar menu item when
 *     the consumer passes an onBack handler (used for inner screens
 *     like Settings, Compose, Templates, Detail, Analytics, Report)
 *   - Feedback messages bubble through the standard FeedbackBar
 */
function Shell({
  children,
  pageName,
  onBack,
  menuItems,
  feedback,
  onCloseFeedback,
}) {
  // Toolbar layout: [Back?, ...screen-specific icons]
  // TopToolbar renders these as Material-UI IconButtons inline with the
  // page title, matching how Note / EmployeeSession / Customer show
  // their per-screen actions.
  const isDesktop = useIsDesktop();
  const items = [];
  if (onBack) {
    items.push({ title: 'Back', icon: <ArrowBackIcon />, onClick: onBack });
  }
  if (menuItems && menuItems.length) items.push(...menuItems);
  return (
    <React.Fragment>
      <TopToolbar
        pageName={pageName || 'NuVe Boost'}
        menuItems={items.length ? items : null}
      />
      <FeedbackBar
        open={!!feedback?.open}
        handleClose={onCloseFeedback}
        severity={feedback?.severity}
        message={feedback?.message}
      />
      <div
        style={{
          background: C.bg,
          minHeight: 'calc(100vh - 64px)',
          fontFamily: "-apple-system,'Segoe UI',sans-serif",
        }}
      >
        <div
          style={{
            // Fill the full viewport width on large screens instead of
            // centering a 1400px column (which left big empty side gutters).
            width: '100%',
            // Mobile: tight 16px gutters so content isn't boxed in by wide
            // margins. Desktop: keep the roomier 24px side padding.
            padding: isDesktop ? '20px 24px 48px' : '20px 16px 48px',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </div>
      </div>
    </React.Fragment>
  );
}

// ── Small atoms (lifted directly from mockup) ──
// Note: the in-page Nav component is gone — TopToolbar (rendered by
// Shell) now owns the back button + per-screen title + icons.
const Steps = ({ cur, tot }) => (
  <div
    style={{
      display: 'flex',
      gap: 6,
      justifyContent: 'center',
      padding: '14px 0 8px',
    }}
  >
    {Array.from({ length: tot }, (_, i) => (
      <div
        key={i}
        style={{
          width: i === cur ? 26 : 8,
          height: 8,
          borderRadius: 4,
          background:
            i === cur
              ? `linear-gradient(135deg,${C.hStart},${C.hEnd})`
              : i < cur
              ? C.green
              : C.borderLight,
        }}
      />
    ))}
  </div>
);
const Lbl = ({ children, style: s }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 600,
      color: C.textMuted,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      marginBottom: 8,
      ...s,
    }}
  >
    {children}
  </div>
);
const Card = ({ children, style: s }) => (
  <div
    style={{
      background: C.white,
      borderRadius: 16,
      padding: 18,
      border: `1px solid ${C.borderLight}`,
      boxShadow:
        '0 1px 3px rgba(15,23,41,0.04), 0 4px 12px rgba(15,23,41,0.04)',
      ...s,
    }}
  >
    {children}
  </div>
);
const CLbl = ({ children }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 600,
      color: C.textMuted,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: 12,
    }}
  >
    {children}
  </div>
);
const TabBar = ({ tabs, active, onChange }) => (
  <div
    style={{
      display: 'flex',
      background: C.softBg,
      borderRadius: 10,
      padding: 3,
      marginBottom: 16,
      border: `1px solid ${C.borderLight}`,
    }}
  >
    {tabs.map((t) => (
      <button
        key={t.k}
        onClick={() => onChange(t.k)}
        style={{
          flex: 1,
          padding: '9px 6px',
          background: active === t.k ? C.white : 'transparent',
          border: 'none',
          borderRadius: 7,
          color: active === t.k ? C.text : C.softText,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          boxShadow: active === t.k ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
        }}
      >
        <I d={t.ico} size={13} color={active === t.k ? C.text : C.softText} />
        {t.l}
        {t.badge ? (
          <span
            style={{
              background: t.badgeColor || C.accent,
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              padding: '1px 5px',
              borderRadius: 8,
            }}
          >
            {t.badge}
          </span>
        ) : null}
      </button>
    ))}
  </div>
);
// Responsive attachment thumbnail used by the composer grid and the message
// preview bubble. Scales with the viewport via clamp()/aspect-ratio (no media
// queries needed for inline styles) and degrades gracefully when the image
// can't load — e.g. a Send-Again photo whose UPLOAD_DIR host isn't reachable
// in this environment — by showing the filename on a neutral tile instead of
// a broken-image box.
const AttachmentThumb = ({ src, name, mode = 'thumb' }) => {
  const [broken, setBroken] = React.useState(false);
  const isBubble = mode === 'bubble';
  // Bubble (preview) shows the whole photo — the box height follows the image
  // so nothing is zoomed, cropped, or cut off at any width. Thumbs stay a
  // fixed square but letterbox instead of cropping.
  const box = isBubble
    ? { width: '100%' }
    : { width: 'clamp(64px, 20vw, 88px)', aspectRatio: '1 / 1' };
  const common = {
    ...box,
    borderRadius: 12,
    overflow: 'hidden',
    boxSizing: 'border-box',
  };
  if (broken || !src) {
    return (
      <div
        style={{
          ...common,
          ...(isBubble ? { aspectRatio: '4 / 3', maxHeight: 280 } : null),
          background: '#eef0f7',
          border: '1px solid #e2e5f1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 6,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#8b90a8',
            lineHeight: 1.25,
            wordBreak: 'break-word',
          }}
        >
          {name || 'Image'}
        </span>
      </div>
    );
  }
  return (
    <div style={{ ...common, background: '#000' }}>
      <img
        src={src}
        alt={name || ''}
        onError={() => setBroken(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </div>
  );
};

const MsgP = ({ g, gT, msg, photos, name, footer = true, locationName }) => {
  if (!msg && (!photos || !photos.length)) return null;
  const sn = name && name.trim() ? name.split(' ')[0] : 'Customer';
  let gl = '';
  if (g === 'generic') gl = gT || 'Hello!';
  else if (g === 'name') gl = `${(gT || 'Hello').replace(/!$/, '')} ${sn}!`;
  return (
    <div
      style={{
        background: '#e8f5e9',
        borderRadius: 18,
        borderBottomLeftRadius: 4,
        overflow: 'hidden',
        boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
      }}
    >
      {photos && photos.length > 0 && (
        <div
          style={{
            display: 'grid',
            // One photo fills the bubble; multiple tile into a responsive
            // 2-up grid that reflows on narrow (mobile) widths.
            gridTemplateColumns: photos.length === 1 ? '1fr' : 'repeat(2, 1fr)',
            gap: 2,
            padding: 2,
          }}
        >
          {photos.map((p) => (
            <AttachmentThumb
              key={p.id}
              src={p.url || p.localURL}
              name={p.name || p.displayName}
              mode="bubble"
            />
          ))}
        </div>
      )}
      <div style={{ padding: '12px 14px' }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: '#1a1d2e',
            marginBottom: 3,
          }}
        >
          {locationName}
        </div>
        {gl && (
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#1a1d2e',
              marginBottom: 4,
            }}
          >
            {gl}
          </div>
        )}
        {msg && (
          <p
            style={{
              margin: '0 0 8px',
              fontSize: 14,
              color: '#1a1d2e',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {msg}
          </p>
        )}
        {footer && (
          <div
            style={{
              fontSize: 10,
              color: '#90a4ae',
              borderTop: '1px solid rgba(0,0,0,0.06)',
              paddingTop: 6,
            }}
          >
            {STOP_FOOTER}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────

function NuVeBoost(props) {
  const {
    userInfo,
    customers: rawCustomers,
    messages: rawMessages,
    templates: rawTemplates,
    credit: creditState,
    settings: settingsState,
    platformConfig,
    sending: globalSending,
    sendResult,
    detail: detailData,
    // actions
    fetchBoostHistory,
    fetchBoostCustomers,
    fetchBoostTemplates,
    fetchBoostCredit,
    fetchBoostSettings,
    fetchBoostPlatformConfig,
    fetchBoostDetail,
    sendBoostBulk,
    retryBoostMessage,
    retryBoostRecipient,
    cancelBoostMessage,
    saveBoostTemplate,
    deleteBoostTemplate,
    saveBoostSettings,
    saveBoostAdmin,
    resetBoostBalance,
    pauseBoost,
  } = props;

  // Platform config — chip options, rates, monthly phone fee. Falls back
  // to a minimal safe shape during the first paint before the fetch
  // resolves so the UI never reads from undefined.
  const cfg = platformConfig || {};
  const PHONE_FEE = Number(cfg.phoneFee ?? FALLBACK_PHONE_FEE);
  // The $1.15/mo number rent is only actually charged once a location has a
  // provisioned Twilio number (BoostCreditService bills only locations whose
  // fromNumber is set; a paused/unfunded/brand-new location has none). So a
  // location with no number owes $0 this month — don't show the fee for it.
  const PHONE_FEE_CHARGED = creditState?.fromNumber ? PHONE_FEE : 0;
  const SMS_BASE = Number(cfg.smsBase ?? FALLBACK_SMS_BASE);
  const MMS_BASE = Number(cfg.mmsBase ?? FALLBACK_MMS_BASE);
  const CREDIT_LIMIT_OPTIONS = (cfg.creditLimitOptions || []).map(Number);
  const LOW_THRESHOLD_OPTIONS = (cfg.lowThresholdOptions || []).map(Number);
  const RECHARGE_OPTIONS = (cfg.rechargeOptions || []).map(Number);
  const MARKUP_FIXED_OPTIONS = (cfg.markupFixedOptions || []).map(Number);
  const MARKUP_PCT_OPTIONS = (cfg.markupPctOptions || []).map(Number);

  const locationId = userInfo?.selectedLocationId;
  const LOC = userInfo?.selectedLocationName || 'Location';
  const isDesktop = useIsDesktop();

  // ── Initial loads ──
  // Platform config is the only call that doesn't depend on locationId
  // — kick it off as soon as the component mounts so the chip arrays
  // are populated before the first render of the admin panel.
  useEffect(() => {
    fetchBoostPlatformConfig();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!locationId) return;
    fetchBoostCustomers({ locationId });
    fetchBoostHistory({ locationId });
    fetchBoostTemplates({ locationId });
    fetchBoostCredit({ locationId });
    fetchBoostSettings({ locationId });
  }, [locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Map backend customers into the mockup's shape ──
  const ALL_CUST = useMemo(
    () =>
      (rawCustomers || []).map((c) => {
        const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
        const av =
          ((c.firstName?.[0] || '') + (c.lastName?.[0] || '')).toUpperCase() ||
          'NA';
        return {
          id: c.customerId,
          name,
          phone: c.phone || '',
          match: c.points || 0,
          av,
          lastDays: c.lastDays || 0,
          status: c.isBlocked
            ? 'blacklist'
            : c.isInactive
            ? 'inactive'
            : 'active',
          phoneValid: c.phoneValid !== false && isValidPhone(c.phone),
          optedOut: !!c.optedOut,
          optedOutAt: c.optedOutAt || null,
        };
      }),
    [rawCustomers]
  );
  const CUST = useMemo(
    () => ALL_CUST.filter((c) => c.status !== 'blacklist' && !c.optedOut),
    [ALL_CUST]
  );
  const BLACKED = useMemo(
    () => ALL_CUST.filter((c) => c.status === 'blacklist'),
    [ALL_CUST]
  );

  // ── Map messages history into the mockup's history shape ──
  const hist = useMemo(
    () =>
      (rawMessages || []).map((m) => {
        // Raw epoch ms so the analytics screen can group by calendar
        // year + month without re-parsing the formatted display string.
        const ts = m.sentAt || m.createdAt || 0;
        return {
          id: m.messageId,
          iTitle: m.title,
          msg: m.body,
          date: fmtDate(ts),
          tsMs: Number(ts) || 0,
          aud: audienceLabelFromCount(m),
          audType: m.audienceType,
          n: m.recipientCount || 0,
          attempted: (m.deliveredCount || 0) + (m.failedCount || 0),
          delivered: m.deliveredCount || 0,
          failed: m.failedCount || 0,
          img: !!m.hasPhotos,
          status: m.status,
          attempts: m.attemptCount || 1,
          // Full composer selections snapshot — lets Send Again restore
          // the exact audience instead of forcing a re-select.
          audience: m.audience || null,
          optOuts: m.optOutCount || 0,
          greeting: m.greeting,
          gText: m.greetingText,
          footer: m.footerEnabled,
          // Server returns [{displayName, path, localURL}] — same shape
          // as customer/machine pictures; localURL is the nginx-served
          // UPLOAD_DIR + path, used directly in history/detail previews.
          photos: (m.photos || [])
            .filter((p) => p && (p.localURL || p.path))
            .map((p, i) => ({
              id: `${m.messageId}-${i}`,
              url: p.localURL,
              // Keep path + name so Send Again can resend the already-stored
              // photo (the backend reuses it from disk — no re-upload needed).
              path: p.path,
              name: p.displayName,
              existing: true,
            })),
          cost: Number(m.cost || 0),
          invalidSkipped: m.invalidSkipped || 0,
        };
      }),
    [rawMessages]
  );

  const templates = useMemo(
    () =>
      (rawTemplates || []).map((t) => ({
        id: t.templateId,
        name: t.name,
        msg: t.body,
        greeting: t.greeting,
        gText: t.greetingText,
        // Server returns [{displayName, path, localURL}] — same shape as
        // message photos; carry path so loadTpl can resend straight from disk.
        photos: (t.photos || [])
          .filter((p) => p && (p.localURL || p.path))
          .map((p, i) => ({
            id: `${t.templateId}-${i}`,
            url: p.localURL,
            path: p.path,
            name: p.displayName,
            existing: true,
          })),
      })),
    [rawTemplates]
  );

  // ── Credit / settings state from Redux ──
  // Fallbacks are 0 (NOT fabricated numbers) so a location that hasn't
  // loaded yet — or a genuinely unfunded one — never flashes a fake
  // available balance. `creditLoaded` distinguishes "still fetching" from
  // "loaded and empty" so we don't show the red "No credit" banner while
  // the first /boost/creditStatus call is still in flight.
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const creditLoaded = !!creditState;
  const creditLimit = Number(creditState?.creditLimit ?? 0);
  const creditUsed = Number(creditState?.creditUsed ?? 0);
  const lowThreshold = Number(creditState?.lowThreshold ?? 0);
  const creditRemaining = creditLimit - creditUsed;
  const creditPct = creditLimit > 0 ? (creditRemaining / creditLimit) * 100 : 0;
  const creditLow = creditRemaining <= lowThreshold && creditRemaining > 0;
  const creditEmpty = creditLoaded && creditRemaining <= 0;
  const moduleActive = creditState?.moduleActive !== false;
  // Platform-owner hard pause: the location can't send OR recharge until
  // NuVe System re-enables it. Distinct from creditEmpty (just out of money).
  const locked = !moduleActive;
  // Per-location 5-minute send cooldown — active while the local countdown
  // (seeded from the backend's cooldownRemainingSeconds) still has time left
  // after a recent send from this location by any staff member.
  const cooldownActive = cooldownLeft > 0;
  // sendBlocked gates EVERY messaging action (send/schedule/quick, history
  // resend + retry, and template creation) so a paused, out-of-credit, or
  // cooling-down location can't message at all.
  const sendBlocked = creditEmpty || locked || cooldownActive;
  const blockedReason = locked
    ? 'NuVe Boost is paused by NuVe System'
    : creditEmpty
    ? 'No credit — please recharge to send'
    : cooldownActive
    ? `Please wait ${Math.floor(cooldownLeft / 60)}:${String(
        cooldownLeft % 60
      ).padStart(2, '0')} before sending another message`
    : '';

  // ── Local UI state (mirrors mockup, minus the data the backend owns) ──
  const [screen, setScreen] = useState('home');
  const [homeTab, setHomeTab] = useState('messages');
  const [step, setStep] = useState(0);
  const [msg, setMsg] = useState('');
  const [iTitle, setITitle] = useState('');
  const [photos, setPhotos] = useState([]);
  const [audType, setAudType] = useState(null);
  const [selMatch, setSelMatch] = useState([]);
  const [vipDes, setVipDes] = useState([]);
  const [selCust, setSelCust] = useState([]);
  const [selInactive, setSelInactive] = useState([]);
  // Inactive/dormant segment — the selected "not visited in N days" chip
  // (null | 30 | 60 | 90). It snapshots the matching customer ids into
  // selInactive at click time (which is what getRec/payload consume); the
  // chip value is kept separately so the highlight round-trips through
  // Send Again + history restore.
  const [inactiveDays, setInactiveDays] = useState(null);
  const [audOpen, setAudOpen] = useState('');
  const [search, setSearch] = useState('');
  const [sendMode, setSendMode] = useState('now');
  const [sDate, setSDate] = useState('');
  const [sTime, setSTime] = useState('');
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [sHour, setSHour] = useState(10);
  const [sMin, setSMin] = useState(0);
  const [sAmpm, setSAmpm] = useState('AM');
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [saveTplName, setSaveTplName] = useState('');

  // Settings (greeting/footer) — start from backend then allow edits
  const [greeting, setGreeting] = useState('none');
  const [gText, setGText] = useState('Hello!');
  const [isCustomG, setIsCustomG] = useState(false);
  const [customG, setCustomG] = useState('');
  // Footer (the "Reply STOP to unsubscribe" line) is always on — it's the
  // opt-out mechanism and can't be turned off, so it's a constant rather than
  // togglable state. Every send/preview/save uses this fixed `true`.
  const footer = true;
  useEffect(() => {
    if (!settingsState) return;
    setGreeting(settingsState.greeting || 'none');
    setGText(settingsState.greetingText || 'Hello!');
  }, [settingsState]);

  // Admin panel local state (mirrored to backend on Save)
  const [markup, setMarkup] = useState(0.02);
  const [markupType, setMarkupType] = useState('fixed');
  const [markupPct, setMarkupPct] = useState(35);
  const [acctType, setAcctType] = useState('global');
  const [chargeMarkup, setChargeMarkup] = useState(true);
  const [adminLowThreshold, setAdminLowThreshold] = useState(10);
  const [adminCreditLimit, setAdminCreditLimit] = useState(50);
  const [adminSaving, setAdminSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);
  useEffect(() => {
    if (settingsState) {
      setMarkup(Number(settingsState.markup ?? 0.02));
      setMarkupType(settingsState.markupType || 'fixed');
      setMarkupPct(settingsState.markupPct ?? 35);
      setAcctType(settingsState.accountType || 'global');
      setChargeMarkup(settingsState.chargeMarkup !== false);
    }
    if (creditState) {
      setAdminLowThreshold(Number(creditState.lowThreshold ?? 10));
      setAdminCreditLimit(Number(creditState.creditLimit ?? 50));
    }
  }, [settingsState, creditState]);

  const [showConfirm, setShowConfirm] = useState(false);
  // Save-confirmation toast for the Settings screen (owner settings + the
  // platform-owner admin panel). Both Save buttons surface a success message
  // through the Shell's FeedbackBar so the owner gets confirmation the save
  // landed instead of the button silently doing nothing visible.
  const [feedback, setFeedback] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const showFeedback = (message, severity = 'success') =>
    setFeedback({ open: true, severity, message });
  const closeFeedback = () => setFeedback((f) => ({ ...f, open: false }));
  const [detailMsg, setDetailMsg] = useState(null);
  // Toggles the per-message "Opted-Out Recipients" list under the Delivery
  // Report (opened by clicking the Opt-outs tile). Reset whenever a new
  // message detail is opened.
  const [showOptOutList, setShowOptOutList] = useState(false);
  // recipientId currently being re-sent from the Failed Recipients list, so
  // that one row's Retry button shows a spinner without disabling the others.
  const [retryingRid, setRetryingRid] = useState(null);
  // Collapse the opt-out list when switching to a different message.
  useEffect(() => {
    setShowOptOutList(false);
  }, [detailMsg]);
  // Fresh detail payload arrived (e.g. after a retry) — clear the row spinner.
  useEffect(() => {
    setRetryingRid(null);
  }, [detailData]);
  const [photoErrs, setPhotoErrs] = useState([]);
  const [histFilter, setHistFilter] = useState('');
  const [quickTab, setQuickTab] = useState('recent');
  const [showRecharge, setShowRecharge] = useState(false);
  // Initialized null on purpose — the useEffect below picks the first
  // option from platformConfig.rechargeOptions once it loads. That way
  // the default tracks whatever ROLE_OWNER set as the lowest tier
  // instead of being hardcoded.
  const [rechargeAmt, setRechargeAmt] = useState(null);
  const [rechargePaying, setRechargePaying] = useState(false); // chip selection → Stripe form
  const [rechargeDone, setRechargeDone] = useState(false);

  // Sync default to the first recharge option whenever platform config
  // loads or changes. Only overrides the user's selection if their
  // current pick has been removed from the options list.
  useEffect(() => {
    if (RECHARGE_OPTIONS.length === 0) return;
    if (rechargeAmt == null || !RECHARGE_OPTIONS.includes(rechargeAmt)) {
      setRechargeAmt(RECHARGE_OPTIONS[0]);
    }
  }, [platformConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // When a recharge succeeds, the Stripe webhook fires async to credit
  // our ledger. We don't block the success screen on it (the customer's
  // money is already with Stripe at this point), but we DO want the
  // dashboard's "remaining balance" to reflect the top-up without
  // forcing a manual refresh. Two spaced refetches handle the typical
  // case where the webhook arrives 1-3s after confirmPayment resolves.
  // Three calls total — enough headroom for slow webhook deliveries
  // without DOSing the endpoint.
  useEffect(() => {
    if (!rechargeDone || !locationId) return undefined;
    // Immediate refetch (covers fast webhook delivery)
    fetchBoostCredit({ locationId });
    const t1 = setTimeout(() => fetchBoostCredit({ locationId }), 2000);
    const t2 = setTimeout(() => fetchBoostCredit({ locationId }), 5000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [rechargeDone, locationId]); // eslint-disable-line react-hooks/exhaustive-deps
  const fileRef = useRef(null);

  // ── Send outcome → navigation ──
  // doSend arms this ref; when the saga publishes the API result we
  // either advance to the success screen (ok) or stay on Review so the
  // owner sees the error toast + corrected balance (failed). This
  // replaces the old optimistic setTimeout that showed "Sent!" even
  // when the server returned an error.
  const awaitingSend = useRef(false);
  useEffect(() => {
    if (!awaitingSend.current || !sendResult) return;
    awaitingSend.current = false;
    setShowConfirm(false);
    if (sendResult.ok) {
      setScreen('sent');
    }
  }, [sendResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Send cooldown (per-location, 5 min) ──
  // Seed the countdown from the backend whenever credit refreshes (mount,
  // after a send/retry, after recharge) so the timer is correct across
  // reloads and reflects sends made by other staff at the same location.
  useEffect(() => {
    const secs = Number(creditState?.cooldownRemainingSeconds ?? 0);
    setCooldownLeft(secs > 0 ? secs : 0);
  }, [creditState]);

  // Tick the cooldown down to zero once per second while it's active.
  useEffect(() => {
    if (!cooldownActive) return undefined;
    const t = setInterval(() => {
      setCooldownLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownActive]);

  // After any send/retry finishes (globalSending falling edge), refetch
  // credit so the freshly-started cooldown window (and new balance) show up
  // immediately — including a send the server rejected for cooldown.
  const prevSendingRef = useRef(false);
  useEffect(() => {
    if (prevSendingRef.current && !globalSending && locationId) {
      fetchBoostCredit({ locationId });
    }
    prevSendingRef.current = globalSending;
  }, [globalSending, locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the open Detail screen in sync with refreshed history —
  // after a Retry Failed round-trip the same message's status/counts
  // change (failed → delivered on success, attempt count bumps).
  useEffect(() => {
    if (!detailMsg) return;
    const fresh = hist.find((h) => h.id === detailMsg.id);
    if (
      fresh &&
      (fresh.status !== detailMsg.status ||
        fresh.attempts !== detailMsg.attempts ||
        fresh.delivered !== detailMsg.delivered ||
        fresh.failed !== detailMsg.failed)
    ) {
      setDetailMsg(fresh);
    }
  }, [hist]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived ──
  const sending = globalSending;
  const allMatch = useMemo(
    () => [...new Set(CUST.map((c) => c.match))].sort((a, b) => b - a),
    [CUST]
  );
  const validCust = useMemo(() => CUST.filter((c) => c.phoneValid), [CUST]);
  const invalidCust = useMemo(() => CUST.filter((c) => !c.phoneValid), [CUST]);
  const vipAll = useMemo(
    () =>
      selMatch.length === 0
        ? []
        : CUST.filter((c) => selMatch.includes(c.match)),
    [selMatch, CUST]
  );
  const vipFinal = useMemo(
    () => vipAll.filter((c) => !vipDes.includes(c.id)),
    [vipAll, vipDes]
  );
  const scheduledCount = useMemo(
    () => hist.filter((h) => h.status === 'scheduled').length,
    [hist]
  );
  const totalOptOuts = useMemo(
    () => hist.reduce((s, h) => s + (h.optOuts || 0), 0),
    [hist]
  );
  // Customers currently on the STOP list for this location — the standing
  // "who has opted out" set (distinct from totalOptOuts, which tallies
  // opt-out EVENTS across message history). Drives the Opt-outs card list.
  const optedOutCust = useMemo(
    () =>
      ALL_CUST.filter((c) => c.optedOut).sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      ),
    [ALL_CUST]
  );
  // "This Month" spend — count ONLY messages that actually consumed
  // credit. 'scheduled' is excluded because credit is deducted at FIRE
  // time (cancelling beforehand costs $0 — this card must not show a
  // charge that never happened). 'cancelled' never charged, and
  // 'failed' sends are auto-refunded by the backend (transport-level
  // failures never reach Twilio).
  const SPEND_STATUSES = ['sending', 'delivered', 'partial_failure'];
  const monthSpend = useMemo(() => {
    const cm = ms[new Date().getMonth()];
    let t = 0;
    let count = 0;
    let r = 0;
    hist.forEach((h) => {
      if (
        SPEND_STATUSES.includes(h.status) &&
        h.date &&
        h.date.startsWith(cm)
      ) {
        t += h.cost || 0;
        count++;
        r += h.n || 0;
      }
    });
    return { total: t, count, recipients: r };
  }, [hist]); // eslint-disable-line react-hooks/exhaustive-deps

  const getRec = () => {
    if (audType === 'all') return CUST;
    const ids = new Set();
    vipFinal.forEach((c) => ids.add(c.id));
    selInactive.forEach((id) => ids.add(id));
    selCust.forEach((id) => ids.add(id));
    return CUST.filter((c) => ids.has(c.id));
  };
  const getValid = () => getRec().filter((c) => c.phoneValid);
  const getInvalid = () => getRec().filter((c) => !c.phoneValid);
  const audCnt = () => getValid().length;
  const mixCount = () => ({
    vip: vipFinal.length,
    inactive: selInactive.length,
    ind: selCust.filter(
      (id) => !vipFinal.some((c) => c.id === id) && !selInactive.includes(id)
    ).length,
  });
  const audLbl = () => {
    if (audType === 'all')
      return `All Customers${
        BLACKED.length > 0 ? ` (${BLACKED.length} blacklisted excluded)` : ''
      }`;
    const m = mixCount();
    const parts = [];
    if (m.vip > 0) parts.push(`${m.vip} VIP`);
    if (m.inactive > 0) parts.push(`${m.inactive} Inactive`);
    if (m.ind > 0) parts.push(`${m.ind} Individual`);
    return parts.join(' + ') || 'No selection';
  };
  // Use the live platform-config rates (SMS_BASE/MMS_BASE, themselves
  // safely defaulted) so the cost preview and canAfford check match what
  // BoostCostService charges server-side. Hardcoding the rates here let the
  // preview drift from the real charge whenever an owner changed the rates.
  const twilioPer = () => (photos.length > 0 ? MMS_BASE : SMS_BASE);
  const markupAmt = () =>
    !chargeMarkup
      ? 0
      : markupType === 'fixed'
      ? markup
      : twilioPer() * (markupPct / 100);
  const costPer = () => twilioPer() + markupAmt();
  const estCost = () => (costPer() * audCnt()).toFixed(2);
  // Segment/encoding estimate is computed on the FULL assembled body (what
  // Twilio bills), then the textarea cap is whatever budget is left after
  // the location line, greeting and STOP footer take their share.
  const segs = smsInfo(assembleSms(LOC, greeting, gText, msg, footer));
  const msgUnits = encodedLen(msg, segs.uni);
  const msgLimit = Math.max(10, segs.lim - (segs.c - msgUnits));
  const ok0 = msg.trim() || photos.length > 0;
  const ok1 = audType === 'all' || getRec().length > 0;
  const okSend = sendMode === 'now' || (sDate && sTime);
  const fmtSched = () => {
    if (!sDate || !sTime) return null;
    try {
      return new Date(`${sDate}T${sTime}`).toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (e) {
      return null;
    }
  };

  // ── Actions ──
  const reset = () => {
    setStep(0);
    setMsg('');
    setITitle('');
    setPhotos([]);
    setAudType(null);
    setSelMatch([]);
    setVipDes([]);
    setSelCust([]);
    setSelInactive([]);
    setInactiveDays(null);
    setAudOpen('');
    setSearch('');
    setSendMode('now');
    setSDate('');
    setSTime('');
    setSHour(10);
    setSMin(0);
    setSAmpm('AM');
    setShowConfirm(false);
    setPhotoErrs([]);
    setShowSaveTpl(false);
    setSaveTplName('');
  };

  // Inactive/dormant helpers. "Not visited in N days" is a cumulative
  // threshold on lastDays (days since last visit, computed server-side), so a
  // single chip is selected at a time — 30+ is the broadest set, 90+ the
  // narrowest. Only messageable customers (CUST already drops blacklisted +
  // opted-out) are eligible; invalid phones get filtered at send like VIP.
  const inactiveMatch = (days) => CUST.filter((c) => (c.lastDays || 0) >= days);
  const pickInactive = (days) => {
    if (audType === 'all') setAudType(null);
    if (inactiveDays === days) {
      setInactiveDays(null);
      setSelInactive([]);
    } else {
      setInactiveDays(days);
      setSelInactive(inactiveMatch(days).map((c) => c.id));
    }
  };

  // Map our local audience UI state into the backend's audience payload.
  const buildAudiencePayload = () => {
    if (audType === 'all') return { audienceType: 'all' };
    return {
      audienceType: 'mix',
      selMatch,
      vipExcluded: vipDes,
      selInactive,
      // Carried for Send Again / history restore only — the backend resolves
      // the inactive segment from selInactive (explicit ids), not this.
      inactiveDays,
      selCust,
    };
  };

  const doSend = () => {
    if (sendBlocked) return; // paused or out of credit — no sends of any kind
    let sendAt = null;
    if (sendMode === 'schedule' && sDate && sTime) {
      try {
        sendAt = new Date(`${sDate}T${sTime}`).getTime();
      } catch (e) {
        sendAt = null;
      }
    }
    sendBoostBulk({
      locationId,
      locationName: LOC, // prepended as the first line of the received SMS/MMS
      title: iTitle?.trim() || null,
      body: msg,
      photos, // compressed JPEG data URLs — stored server-side, sent as MMS media
      greeting,
      // 'No Greeting' sends no text at all — don't ship the stale
      // "Hello!" default; it's ignored server-side but confuses the
      // payload and the stored message doc.
      greetingText: greeting === 'none' ? null : gText,
      footerEnabled: footer,
      audience: buildAudiencePayload(),
      sendMode,
      sendAt,
    });
    // NO optimistic navigation — the review screen shows its spinner
    // while `sending` is true, and the effect below moves to the
    // success screen only when the API actually reports success.
    awaitingSend.current = true;
  };

  const cancelSch = (id) => {
    cancelBoostMessage({ messageId: id, locationId });
    if (detailMsg?.id === id)
      setDetailMsg({ ...detailMsg, status: 'cancelled' });
  };
  const applyTpl = (h) => {
    if (sendBlocked) return;
    reset();
    setMsg(h.msg || '');
    setITitle(h.iTitle ? `${h.iTitle} (copy)` : '');
    setGreeting(h.greeting || 'none');
    setGText(h.gText || 'Hello!');
    setPhotos(
      (h.photos || []).map((p) => ({
        id: p.id || `copy-${Date.now()}-${Math.random()}`,
        url: p.url,
        path: p.path,
        name: p.name,
        existing: true,
      }))
    );
    setScreen('compose');
    setStep(0);
  };
  const editSch = (h) => {
    cancelBoostMessage({ messageId: h.id, locationId });
    reset();
    setMsg(h.msg || '');
    setITitle(h.iTitle || '');
    setGreeting(h.greeting || 'none');
    setGText(h.gText || 'Hello!');
    setSendMode('schedule');
    setDetailMsg(null);
    setScreen('compose');
    setStep(0);
  };
  const sendAgain = (h) => {
    if (sendBlocked) return;
    reset();
    setMsg(h.msg || '');
    setITitle(h.iTitle || '');
    setGreeting(h.greeting || 'none');
    setGText(h.gText || 'Hello!');
    setAudType(h.audType === 'all' ? 'all' : null);
    // Restore the exact audience the original send used (spec §15) —
    // VIP match chips, VIP exclusions, inactive picks, individuals.
    if (h.audience && h.audType !== 'all') {
      setSelMatch(h.audience.selMatch || []);
      setVipDes(h.audience.vipExcluded || []);
      setSelInactive(h.audience.selInactive || []);
      setInactiveDays(h.audience.inactiveDays || null);
      setSelCust(h.audience.selCust || []);
    }
    // Restore the attachment too — the original send's photo carries over so
    // the resend goes out as MMS, not a downgraded text. Existing photos
    // resend straight from disk (no re-upload), so we only need the path.
    setPhotos(
      (h.photos || []).map((p) => ({
        id: p.id || `again-${Date.now()}-${Math.random()}`,
        url: p.url,
        path: p.path,
        name: p.name,
        existing: true,
      }))
    );
    setDetailMsg(null);
    setScreen('compose');
    setStep(1);
  };
  const loadTpl = (t) => {
    if (sendBlocked) return;
    reset();
    setMsg(t.msg);
    setGreeting(t.greeting || 'none');
    setGText(t.gText || 'Hello!');
    setITitle(t.name);
    // Restore the template's attachments into the composer.
    setPhotos(
      (t.photos || []).map((p) => ({
        id: p.id || `tpl-${Date.now()}-${Math.random()}`,
        url: p.url,
        path: p.path,
        name: p.name,
        existing: true,
      }))
    );
    setScreen('compose');
    setStep(0);
  };
  const saveTplAct = () => {
    if (sendBlocked) return; // no template creation while paused / out of credit
    if (!saveTplName.trim() || !msg.trim()) return;
    saveBoostTemplate({
      locationId,
      name: saveTplName.trim(),
      body: msg,
      greeting,
      greetingText: gText,
      // Persist attachments with the template (base64 for new uploads, path
      // for already-stored ones) so they come back when the template loads.
      photos,
    });
    setShowSaveTpl(false);
    setSaveTplName('');
  };
  const delTpl = (id) => deleteBoostTemplate({ templateId: id, locationId });

  const handleFile = (e) => {
    const files = Array.from(e.target.files || []);
    const errs = [];
    files.slice(0, 4 - photos.length).forEach((f) => {
      if (f.size > MAX_MB * 1024 * 1024) {
        errs.push(`${f.name} exceeds ${MAX_MB}MB`);
        return;
      }
      const r = new FileReader();
      r.onload = (ev) =>
        // Compress before storing so the preview matches exactly what
        // the backend stores and Twilio delivers.
        compressPhoto(ev.target.result, f.type).then((url) =>
          setPhotos((p) =>
            p.length >= 4
              ? p
              : [
                  ...p,
                  {
                    id: Date.now() + Math.random(),
                    url,
                    name: f.name,
                  },
                ]
          )
        );
      r.readAsDataURL(f);
    });
    setPhotoErrs(errs.length ? errs : []);
    if (e.target) e.target.value = '';
  };
  const togM = (v) => {
    setSelMatch((p) => {
      if (p.includes(v)) {
        setVipDes((d) =>
          d.filter(
            (id) =>
              !CUST.filter((c) => c.match === v)
                .map((c) => c.id)
                .includes(id)
          )
        );
        return p.filter((x) => x !== v);
      }
      return [...p, v];
    });
  };
  const togAll = () => {
    setSelMatch((p) => (p.length === allMatch.length ? [] : [...allMatch]));
    setVipDes([]);
  };
  const openRecharge = () => {
    setShowRecharge(true);
    setRechargePaying(false);
    setRechargeDone(false);
  };
  const closeRecharge = () => {
    setShowRecharge(false);
    setRechargePaying(false);
    setRechargeDone(false);
  };

  // Calendar helpers
  const mNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const dNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const calFirstDay = new Date(calYear, calMonth, 1).getDay();
  const calDaysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const calTodayStr = new Date().toISOString().split('T')[0];
  const calCells = useMemo(() => {
    const c = [];
    for (let i = 0; i < calFirstDay; i++) c.push(null);
    for (let d = 1; d <= calDaysInMonth; d++) c.push(d);
    return c;
  }, [calFirstDay, calDaysInMonth]);
  const calPrev = () => {
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear(calYear - 1);
    } else setCalMonth(calMonth - 1);
  };
  const calNext = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear(calYear + 1);
    } else setCalMonth(calMonth + 1);
  };
  const calPick = (d) => {
    const mm = String(calMonth + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    const ds = `${calYear}-${mm}-${dd}`;
    setSDate(ds);
    const h24 =
      sAmpm === 'PM'
        ? sHour === 12
          ? 12
          : sHour + 12
        : sHour === 12
        ? 0
        : sHour;
    setSTime(`${h24 < 10 ? '0' : ''}${h24}:${sMin < 10 ? '0' : ''}${sMin}`);
  };
  const calTime = (h, m, ap) => {
    setSHour(h);
    setSMin(m);
    setSAmpm(ap);
    if (sDate) {
      const h24 = ap === 'PM' ? (h === 12 ? 12 : h + 12) : h === 12 ? 0 : h;
      setSTime(`${h24 < 10 ? '0' : ''}${h24}:${m < 10 ? '0' : ''}${m}`);
    }
  };

  // Modals
  // Rendered through a portal to <body>: the modal lives deep inside the
  // app shell, whose ancestors (rightContainer z-index:5, fullWidthContainer
  // z-index:1) create stacking contexts that TRAP the overlay below the
  // mobile header/drawer (z-index:1000) no matter how high the overlay's own
  // z-index. Portaling to document.body escapes those contexts entirely.
  const RechargeModal = () =>
    showRecharge
      ? ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              // Above the app chrome: the mobile header/drawer is z-index 1000
              // (Base.css .drawer-menu !important) and header dropdowns / company
              // picker go to 1300–1400. At 999 the blue nav bar punched through
              // the middle of the payment sheet on mobile. 9999 matches the app's
              // top-level-modal convention (CompanyWizardV3, SplashScreen).
              zIndex: 9999,
              padding: isDesktop ? 20 : 0,
              // Desktop: center the dialog vertically + horizontally.
              // Mobile: pin to bottom edge (iOS bottom-sheet look).
              alignItems: isDesktop ? 'center' : 'flex-end',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                background: C.white,
                // Desktop: fully rounded card; mobile: only the top corners rounded
                // to match the bottom-sheet style.
                borderRadius: isDesktop ? 20 : '24px 24px 0 0',
                padding: '24px 20px 36px',
                width: '100%',
                maxWidth: 460,
                boxShadow: isDesktop
                  ? '0 20px 60px rgba(0,0,0,0.4)'
                  : '0 -10px 40px rgba(0,0,0,0.3)',
              }}
            >
              {/* Step 1 — amount selection */}
              {!rechargePaying && !rechargeDone && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 20,
                    }}
                  >
                    <div
                      style={{ fontSize: 18, fontWeight: 700, color: C.text }}
                    >
                      Recharge Credit
                    </div>
                    <button
                      onClick={closeRecharge}
                      style={{
                        background: C.inputBg,
                        border: 'none',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <I d={ic.x} size={16} color={C.textMuted} />
                    </button>
                  </div>
                  <div
                    style={{
                      textAlign: 'center',
                      marginBottom: 20,
                      padding: 16,
                      background: C.inputBg,
                      borderRadius: 14,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: C.textMuted,
                        marginBottom: 4,
                      }}
                    >
                      Current Balance
                    </div>
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: creditEmpty ? C.red : C.orange,
                      }}
                    >
                      ${creditRemaining.toFixed(2)}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.textMuted,
                      textTransform: 'uppercase',
                      marginBottom: 8,
                    }}
                  >
                    Select Amount
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    {RECHARGE_OPTIONS.map((v) => (
                      <button
                        key={v}
                        onClick={() => setRechargeAmt(v)}
                        style={{
                          flex: 1,
                          padding: '14px 4px',
                          background: rechargeAmt === v ? C.accent : C.inputBg,
                          border: `2px solid ${
                            rechargeAmt === v ? C.accent : C.border
                          }`,
                          borderRadius: 12,
                          color: rechargeAmt === v ? '#fff' : C.text,
                          fontSize: 16,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        ${v}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setRechargePaying(true)}
                    disabled={!rechargeAmt || rechargeAmt <= 0}
                    style={{
                      width: '100%',
                      padding: 14,
                      background: rechargeAmt > 0 ? grad : dis,
                      border: 'none',
                      borderRadius: 12,
                      color: rechargeAmt > 0 ? '#fff' : disTxt,
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: rechargeAmt > 0 ? 'pointer' : 'default',
                    }}
                  >
                    Continue — ${(rechargeAmt || 0).toFixed(2)}
                  </button>
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 10,
                      color: C.textMuted,
                      textAlign: 'center',
                    }}
                  >
                    Card, Apple Pay, and Google Pay accepted. Processed securely
                    by Stripe.
                  </div>
                </>
              )}

              {/* Step 2 — Stripe PaymentElement (card + Apple Pay / Google Pay) */}
              {rechargePaying && !rechargeDone && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                    }}
                  >
                    <div>
                      <div
                        style={{ fontSize: 18, fontWeight: 700, color: C.text }}
                      >
                        Add ${(rechargeAmt || 0).toFixed(2)} credit
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.textMuted,
                          marginTop: 2,
                        }}
                      >
                        Processing fee shown below · added to your NuVe Boost
                        credit
                      </div>
                    </div>
                    <button
                      onClick={closeRecharge}
                      style={{
                        background: C.inputBg,
                        border: 'none',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <I d={ic.x} size={16} color={C.textMuted} />
                    </button>
                  </div>
                  <BoostRechargeStripe
                    locationId={locationId}
                    locationName={LOC}
                    amount={Number(rechargeAmt)}
                    onPaymentApplied={() => {
                      setRechargeDone(true);
                    }}
                    onCancel={() => setRechargePaying(false)}
                  />
                </>
              )}

              {/* Step 3 — success */}
              {rechargeDone && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div
                    style={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      background: gradG,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      boxShadow: `0 8px 30px ${C.greenGlow}`,
                    }}
                  >
                    <I d={ic.check} size={34} color="#fff" />
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: C.text,
                      marginBottom: 6,
                    }}
                  >
                    Payment Successful!
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      color: C.textMuted,
                      marginBottom: 16,
                    }}
                  >
                    ${(rechargeAmt || 0).toFixed(2)} added to your balance
                  </div>
                  <button
                    onClick={closeRecharge}
                    style={{
                      width: '100%',
                      padding: 14,
                      background: gradG,
                      border: 'none',
                      borderRadius: 12,
                      color: '#fff',
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  const RechargeBtn = ({ large }) => (
    <button
      onClick={openRecharge}
      style={{
        width: '100%',
        padding: large ? 13 : 11,
        background: 'linear-gradient(135deg,#000,#1a1a2e)',
        border: 'none',
        borderRadius: large ? 12 : 10,
        color: '#fff',
        fontSize: large ? 14 : 13,
        fontWeight: large ? 700 : 600,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        boxShadow: large ? '0 4px 15px rgba(0,0,0,0.3)' : 'none',
      }}
    >
      <span
        role="img"
        aria-label="credit card"
        style={{ fontSize: large ? 17 : 15 }}
      >
        💳
      </span>{' '}
      {large ? 'Recharge Now' : 'Recharge Credit'}
    </button>
  );

  const ConfirmModal = () =>
    showConfirm
      ? ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Above the app chrome (mobile header/drawer z-index 1000, header
              // dropdowns/company picker 1300–1400) — same reason as RechargeModal.
              zIndex: 9999,
              padding: 20,
            }}
          >
            <div
              style={{
                background: C.white,
                borderRadius: 20,
                padding: 24,
                maxWidth: 320,
                width: '100%',
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background: C.orangePale,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                  }}
                >
                  <I d={ic.alert} size={24} color={C.orange} />
                </div>
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: C.text,
                    marginBottom: 6,
                  }}
                >
                  Confirm
                </div>
                <div
                  style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.5 }}
                >
                  {sendMode === 'now' ? 'Send' : 'Schedule'} to {audCnt()}{' '}
                  recipients?
                </div>
              </div>
              <div
                style={{
                  background: C.accentPale,
                  borderRadius: 10,
                  padding: '10px 14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 16,
                }}
              >
                <span style={{ fontSize: 13, color: C.accent }}>
                  Estimated cost
                </span>
                <span
                  style={{ fontSize: 15, fontWeight: 700, color: C.accent }}
                >
                  ~${estCost()}
                </span>
              </div>
              <button
                onClick={doSend}
                style={{
                  width: '100%',
                  padding: 14,
                  background: gradG,
                  border: 'none',
                  borderRadius: 12,
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: 8,
                }}
              >
                {sendMode === 'now' ? 'Yes, Send' : 'Yes, Schedule'}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  width: '100%',
                  padding: 12,
                  background: 'none',
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 12,
                  color: C.textMuted,
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  // ─── Credit Management visibility — ROLE_OWNER (NDS superuser) only.
  // Per spec the platform owner configures markup + credit limit per
  // location; no other role (not even TECH_ADMIN) can edit billing.
  //
  // AccountService.getUserInfo returns roles under `authority` (singular,
  // string array) — same field every other component in the codebase
  // reads (see ReportView.isOwner, Header.jsx, CustomerTable.isOwner).
  // We ALSO honor the User.isOwner boolean for accounts that were
  // bootstrapped before the role cascade ran.
  const ownerRoles = userInfo?.authority || [];
  const isPlatformOwner =
    !!userInfo?.isOwner || ownerRoles.includes('ROLE_OWNER');

  // ══════════════════════════════════════════════════════════════════════
  // SCREEN: Opt-outs — customers currently on the STOP list for this
  // location. View-only: opt-out is customer-initiated (they text STOP);
  // re-subscribing requires the customer to text START, so there is no
  // "remove" action here.
  // ══════════════════════════════════════════════════════════════════════
  if (screen === 'optout') {
    return (
      <Shell pageName="Opted-Out Customers" onBack={() => setScreen('home')}>
        <div style={{ padding: 20 }}>
          <Card style={{ marginBottom: 14 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 4,
              }}
            >
              <CLbl>Opted-Out Customers</CLbl>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.orange,
                  background: C.orangePale,
                  padding: '2px 10px',
                  borderRadius: 10,
                }}
              >
                {optedOutCust.length}
              </span>
            </div>
            <div
              style={{
                fontSize: 11,
                color: C.textMuted,
                lineHeight: 1.5,
                marginBottom: optedOutCust.length ? 12 : 0,
              }}
            >
              These customers replied STOP and are excluded from every send.
              They must text START from their phone to receive messages again.
            </div>
            {optedOutCust.length === 0 ? (
              <div
                style={{
                  padding: '24px 0',
                  textAlign: 'center',
                  fontSize: 13,
                  color: C.textMuted,
                }}
              >
                No customers have opted out.
              </div>
            ) : (
              <div style={{ maxHeight: 460, overflowY: 'auto' }}>
                {optedOutCust.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 0',
                      borderBottom: `1px solid ${C.borderLight}`,
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: C.orangePale,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        color: C.orange,
                        flexShrink: 0,
                      }}
                    >
                      {c.av}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: C.text,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {c.name || 'Unknown'}
                      </div>
                      <div style={{ fontSize: 11, color: C.textMuted }}>
                        {c.phone || '—'}
                      </div>
                    </div>
                    {c.optedOutAt && (
                      <div
                        style={{
                          fontSize: 10,
                          color: C.textMuted,
                          textAlign: 'right',
                          flexShrink: 0,
                        }}
                      >
                        opted out
                        <br />
                        {fmtDate(c.optedOutAt)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </Shell>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // SCREEN: Detail
  // ══════════════════════════════════════════════════════════════════════
  if (screen === 'detail' && detailMsg) {
    const m = detailMsg;
    // Both 'sending' (in flight — delivery webhooks will flip it) and
    // 'partial_failure' (at least one text went through) present as
    // Delivered here, matching the home screen's Delivered bucket
    // (see the dMsgs filter) so a message filed under Delivered doesn't
    // then show an orange "sending" on its detail. The Delivery Report
    // counts and failed-recipients list below tell the rest of the
    // story, and raw m.status still drives the Retry Failed button.
    const displayStatus =
      m.status === 'partial_failure' || m.status === 'sending'
        ? 'delivered'
        : m.status;
    const sc =
      displayStatus === 'delivered'
        ? C.green
        : displayStatus === 'scheduled'
        ? C.orange
        : C.red;
    // Only failed rows are listed — with 1000-customer audiences the
    // delivered list is huge and useless here. The backend already
    // returns failures only; this filter keeps stale/cached payloads
    // honest.
    const detailReady = detailData?.message?.messageId === m.id;
    const failedRecipients = detailReady
      ? (detailData.recipients || []).filter(
          (r) => r.status === 'failed' || r.status === 'undelivered'
        )
      : [];
    // Recipients of THIS message who are on the location STOP list — the
    // "who opted out" behind the Opt-outs count tile.
    const optOutRecipients = detailReady
      ? detailData.optOutRecipients || []
      : [];
    return (
      <Shell
        pageName="Message Detail"
        onBack={() => {
          setDetailMsg(null);
          setScreen('home');
        }}
      >
        <div style={{ padding: 20 }}>
          <Card style={{ marginBottom: 14 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: sc,
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: sc,
                    textTransform: 'capitalize',
                  }}
                >
                  {displayStatus}
                </span>
                {(m.attempts || 1) > 1 && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.textMuted,
                      background: C.softBg,
                      padding: '2px 8px',
                      borderRadius: 8,
                    }}
                  >
                    attempt {m.attempts}
                  </span>
                )}
              </div>
              <span style={{ fontSize: 12, color: C.textMuted }}>{m.date}</span>
            </div>
            <div
              style={{
                fontSize: 17,
                fontWeight: 700,
                color: C.text,
                marginBottom: 4,
              }}
            >
              {m.iTitle}
            </div>
            <div style={{ fontSize: 12, color: C.textMuted }}>
              {m.aud} · {m.n} recipients · ~${(m.cost || 0).toFixed(2)}
            </div>
          </Card>
          <Card style={{ marginBottom: 14 }}>
            <CLbl>Delivery Report</CLbl>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr',
                gap: 8,
              }}
            >
              {[
                { v: m.delivered, l: 'Delivered', c: C.green },
                {
                  v: m.failed,
                  l: 'Failed',
                  c: m.failed > 0 ? C.red : C.textDim,
                },
                { v: m.attempted, l: 'Attempted', c: C.accent },
                {
                  v: m.optOuts || 0,
                  l: 'Opt-outs',
                  c: (m.optOuts || 0) > 0 ? C.orange : C.textDim,
                  // Only clickable when there are named opt-out recipients to
                  // reveal (optOutCount can be > 0 while the who-list is empty
                  // if the STOP came from an unmatched number).
                  onClick:
                    optOutRecipients.length > 0
                      ? () => setShowOptOutList((x) => !x)
                      : null,
                },
              ].map((s, i) => (
                <div
                  key={i}
                  role={s.onClick ? 'button' : undefined}
                  tabIndex={s.onClick ? 0 : undefined}
                  onClick={s.onClick || undefined}
                  onKeyDown={
                    s.onClick
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') s.onClick();
                        }
                      : undefined
                  }
                  title={s.onClick ? 'View who opted out' : undefined}
                  style={{
                    textAlign: 'center',
                    cursor: s.onClick ? 'pointer' : 'default',
                    borderRadius: 8,
                    background:
                      s.onClick && showOptOutList
                        ? C.orangePale
                        : 'transparent',
                  }}
                >
                  <div style={{ fontSize: 20, fontWeight: 700, color: s.c }}>
                    {s.v}
                  </div>
                  <div style={{ fontSize: 10, color: C.textMuted }}>
                    {s.l}
                    {s.onClick && (showOptOutList ? ' ▲' : ' ▾')}
                  </div>
                </div>
              ))}
            </div>
            {(m.invalidSkipped || 0) > 0 && (
              <div
                style={{
                  marginTop: 10,
                  padding: '8px 12px',
                  background: C.orangePale,
                  borderRadius: 8,
                  fontSize: 12,
                  color: C.orange,
                }}
              >
                {m.invalidSkipped} skipped (invalid phone)
              </div>
            )}
          </Card>
          {showOptOutList && optOutRecipients.length > 0 && (
            <Card style={{ marginBottom: 14 }}>
              <CLbl>Opted-Out Recipients ({optOutRecipients.length})</CLbl>
              <div
                style={{
                  fontSize: 11,
                  color: C.textMuted,
                  marginBottom: 8,
                  lineHeight: 1.4,
                }}
              >
                Recipients of this message who have replied STOP and are now
                excluded from future sends.
              </div>
              <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {optOutRecipients.map((r) => (
                  <div
                    key={r.recipientId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 0',
                      borderBottom: `1px solid ${C.borderLight}`,
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: C.orangePale,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.orange,
                        flexShrink: 0,
                      }}
                    >
                      {(r.displayName || '?')
                        .split(' ')
                        .map((w) => w[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: C.text,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {r.displayName || 'Unknown'}
                      </div>
                      <div style={{ fontSize: 11, color: C.textMuted }}>
                        {r.phone}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.orange,
                        background: C.orangePale,
                        padding: '3px 8px',
                        borderRadius: 8,
                        flexShrink: 0,
                      }}
                    >
                      opted out
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {failedRecipients.length > 0 && (
            <Card style={{ marginBottom: 14 }}>
              <CLbl>Failed Recipients ({failedRecipients.length})</CLbl>
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {failedRecipients.map((r) => {
                  const chipColor = C.red;
                  const chipBg = C.redPale;
                  // Plain-English reason from the Twilio code (falls back to
                  // Twilio's own message, then the bare code).
                  const friendly = friendlyTwilioError(
                    r.errorCode,
                    r.errorMessage
                  );
                  // A recipient who replied STOP can't be re-texted — Twilio
                  // rejects it and it's a compliance violation, so no Retry.
                  const isUnsub = String(r.errorCode || '') === '21610';
                  const rowRetrying = retryingRid === r.recipientId;
                  return (
                    <div
                      key={r.recipientId}
                      style={{
                        padding: '8px 0',
                        borderBottom: `1px solid ${C.borderLight}`,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                        }}
                      >
                        {r.photoURL ? (
                          <img
                            src={r.photoURL}
                            alt=""
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: '50%',
                              objectFit: 'cover',
                              flexShrink: 0,
                              border: `1.5px solid ${C.border}`,
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: '50%',
                              background: C.accentPale,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 11,
                              fontWeight: 700,
                              color: C.accent,
                              flexShrink: 0,
                            }}
                          >
                            {(r.displayName || '?')
                              .split(' ')
                              .map((w) => w[0])
                              .join('')
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: C.text,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {r.displayName || 'Unknown'}
                          </div>
                          <div style={{ fontSize: 11, color: C.textMuted }}>
                            {r.phone}
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: chipColor,
                            background: chipBg,
                            padding: '3px 8px',
                            borderRadius: 8,
                            textTransform: 'capitalize',
                            flexShrink: 0,
                          }}
                        >
                          {r.status}
                        </span>
                      </div>
                      <div
                        style={{
                          marginTop: 5,
                          padding: '6px 10px',
                          background: C.redPale,
                          borderRadius: 8,
                          fontSize: 11,
                          color: C.red,
                          lineHeight: 1.4,
                        }}
                      >
                        {friendly}
                        {r.errorCode && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 9,
                              fontWeight: 700,
                              color: C.textMuted,
                              opacity: 0.8,
                            }}
                          >
                            (code {r.errorCode})
                          </span>
                        )}
                        {r.refunded && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontWeight: 700,
                              color: C.green,
                            }}
                          >
                            · refunded
                          </span>
                        )}
                      </div>
                      {isUnsub ? (
                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 10,
                            color: C.textMuted,
                            fontStyle: 'italic',
                          }}
                        >
                          Can't retry — customer must text START to opt back in.
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            if (rowRetrying || sending || sendBlocked) return;
                            setRetryingRid(r.recipientId);
                            retryBoostRecipient({
                              messageId: m.id,
                              recipientId: r.recipientId,
                              locationId,
                            });
                          }}
                          disabled={rowRetrying || sending || sendBlocked}
                          title={
                            sendBlocked
                              ? blockedReason
                              : 'Resend to this recipient'
                          }
                          style={{
                            marginTop: 6,
                            padding: '6px 12px',
                            background:
                              rowRetrying || sending || sendBlocked
                                ? dis
                                : C.accentPale,
                            border: 'none',
                            borderRadius: 8,
                            color:
                              rowRetrying || sending || sendBlocked
                                ? disTxt
                                : C.accent,
                            fontSize: 11,
                            fontWeight: 700,
                            cursor:
                              rowRetrying || sending || sendBlocked
                                ? 'default'
                                : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                          }}
                        >
                          <I
                            d={ic.send}
                            size={12}
                            color={
                              rowRetrying || sending || sendBlocked
                                ? disTxt
                                : C.accent
                            }
                          />
                          {rowRetrying ? 'Retrying…' : 'Retry'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
          <Card style={{ marginBottom: 14 }}>
            <CLbl>Message</CLbl>
            <MsgP
              g={m.greeting}
              gT={m.gText}
              msg={m.msg}
              photos={m.photos || []}
              footer={m.footer}
              name="María"
              locationName={LOC}
            />
          </Card>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(m.status === 'failed' || m.status === 'partial_failure') && (
              <button
                onClick={() => {
                  if (sending || sendBlocked) return;
                  retryBoostMessage({ messageId: m.id, locationId });
                }}
                disabled={sending || sendBlocked}
                title={sendBlocked ? blockedReason : undefined}
                style={{
                  flexBasis: '100%',
                  padding: 12,
                  background: sending || sendBlocked ? dis : gradG,
                  border: 'none',
                  borderRadius: 12,
                  color: sending || sendBlocked ? disTxt : '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: sending || sendBlocked ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  opacity: sendBlocked ? 0.5 : 1,
                }}
              >
                <I
                  d={ic.send}
                  size={14}
                  color={sending || sendBlocked ? disTxt : '#fff'}
                />
                {sending
                  ? 'Retrying…'
                  : `Retry Failed (attempt ${(m.attempts || 1) + 1})`}
              </button>
            )}
            <button
              onClick={() => {
                if (sendBlocked) return;
                sendAgain(m);
              }}
              disabled={sendBlocked}
              title={sendBlocked ? blockedReason : undefined}
              style={{
                flex: 1,
                minWidth: 100,
                padding: 11,
                background: sendBlocked ? C.softBg : C.greenPale,
                border: 'none',
                borderRadius: 12,
                color: sendBlocked ? C.softTextDim : C.green,
                fontSize: 12,
                fontWeight: 600,
                cursor: sendBlocked ? 'default' : 'pointer',
                opacity: sendBlocked ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
              }}
            >
              <I
                d={ic.send}
                size={13}
                color={sendBlocked ? C.softTextDim : C.green}
              />{' '}
              Send Again
            </button>
            <button
              onClick={() => applyTpl(m)}
              style={{
                flex: 1,
                minWidth: 100,
                padding: 11,
                background: C.accentPale,
                border: 'none',
                borderRadius: 12,
                color: C.accent,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
              }}
            >
              <I d={ic.copy} size={13} color={C.accent} /> Template
            </button>
            {m.status === 'scheduled' && (
              <button
                onClick={() => editSch(m)}
                style={{
                  flex: 1,
                  minWidth: 80,
                  padding: 11,
                  background: C.orangePale,
                  border: 'none',
                  borderRadius: 12,
                  color: C.orange,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                }}
              >
                <I d={ic.edit} size={13} color={C.orange} /> Edit
              </button>
            )}
            {m.status === 'scheduled' && (
              <button
                onClick={() => cancelSch(m.id)}
                style={{
                  flex: 1,
                  minWidth: 80,
                  padding: 11,
                  background: C.redPale,
                  border: 'none',
                  borderRadius: 12,
                  color: C.red,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                }}
              >
                <I d={ic.trash} size={13} color={C.red} /> Cancel
              </button>
            )}
          </div>
        </div>
      </Shell>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // SCREEN: Settings (greeting / footer / admin panel)
  // ══════════════════════════════════════════════════════════════════════
  if (screen === 'settings') {
    const gOpts = [
      { k: 'none', l: 'No Greeting', d: 'Straight to content' },
      { k: 'generic', l: 'Generic', d: 'Same for everyone' },
      { k: 'name', l: 'Personalized', d: 'With customer name' },
    ];
    const saveOwnerSettings = () => {
      saveBoostSettings({
        locationId,
        greeting,
        greetingText: gText,
        // Footer is always on (the STOP line is required for opt-out), so it's
        // no longer a togglable setting — always persist it enabled.
        footerEnabled: true,
      });
      showFeedback('Settings saved');
    };
    return (
      <Shell
        pageName="Settings"
        onBack={() => setScreen('home')}
        feedback={feedback}
        onCloseFeedback={closeFeedback}
      >
        {/* Mobile: no extra horizontal padding — Shell already gives a 16px
            gutter, so this wrapper only adds vertical spacing. Desktop keeps
            the roomier all-round 20px. */}
        <div style={{ padding: isDesktop ? 20 : '20px 0' }}>
          <Card style={{ marginBottom: 16 }}>
            <CLbl>Location</CLbl>
            <div
              style={{
                padding: '12px 14px',
                background: C.accentPale,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: grad,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <I d={ic.mega} size={16} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
                  {LOC}
                </div>
                <div style={{ fontSize: 11, color: C.textMuted }}>
                  Auto-detected
                </div>
              </div>
            </div>
          </Card>
          <Card style={{ marginBottom: 16 }}>
            <CLbl>Greeting</CLbl>
            {gOpts.map((o) => (
              <button
                key={o.k}
                onClick={() => setGreeting(o.k)}
                style={{
                  width: '100%',
                  padding: 12,
                  marginBottom: 6,
                  background: greeting === o.k ? C.accentPale : C.inputBg,
                  border: `1.5px solid ${
                    greeting === o.k ? C.accent : 'transparent'
                  }`,
                  borderRadius: 10,
                  cursor: 'pointer',
                  textAlign: 'left',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div
                      style={{ fontSize: 13, fontWeight: 600, color: C.text }}
                    >
                      {o.l}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>
                      {o.d}
                    </div>
                  </div>
                  {greeting === o.k && (
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: C.accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <I d={ic.check} size={10} color="#fff" />
                    </div>
                  )}
                </div>
              </button>
            ))}
            {(greeting === 'generic' || greeting === 'name') && (
              <div
                style={{
                  marginTop: 6,
                  padding: 12,
                  background: C.inputBg,
                  borderRadius: 10,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    flexWrap: 'wrap',
                    marginBottom: 8,
                  }}
                >
                  {['Hello!', 'Hi!', 'Hey!', 'Hola!', 'Hey there!'].map((g) => (
                    <button
                      key={g}
                      onClick={() => {
                        setGText(g);
                        setIsCustomG(false);
                      }}
                      style={{
                        padding: '6px 14px',
                        background:
                          !isCustomG && gText === g ? C.accent : C.white,
                        border: `1.5px solid ${
                          !isCustomG && gText === g ? C.accent : C.border
                        }`,
                        borderRadius: 20,
                        color: !isCustomG && gText === g ? '#fff' : C.text,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {g}
                    </button>
                  ))}
                  <button
                    onClick={() => setIsCustomG(true)}
                    style={{
                      padding: '6px 14px',
                      background: isCustomG ? C.accent : C.white,
                      border: `1.5px solid ${isCustomG ? C.accent : C.border}`,
                      borderRadius: 20,
                      color: isCustomG ? '#fff' : C.text,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <I
                      d={ic.edit}
                      size={11}
                      color={isCustomG ? '#fff' : C.textMuted}
                    />{' '}
                    Custom
                  </button>
                </div>
                {isCustomG && (
                  <input
                    value={customG}
                    onChange={(e) => {
                      setCustomG(e.target.value);
                      setGText(e.target.value || 'Hello!');
                    }}
                    placeholder="Custom greeting..."
                    maxLength={50}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: C.white,
                      border: `1.5px solid ${C.accent}`,
                      borderRadius: 8,
                      color: C.text,
                      fontSize: 13,
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      outline: 'none',
                      marginBottom: 6,
                    }}
                  />
                )}
                <div style={{ fontSize: 12, color: C.textSec }}>
                  Preview:{' '}
                  <strong>
                    {greeting === 'generic'
                      ? gText
                      : `${gText.replace(/!$/, '')} María!`}
                  </strong>
                </div>
                {greeting === 'name' && (
                  <div style={{ marginTop: 6, fontSize: 11, color: C.orange }}>
                    <span role="img" aria-label="warning">
                      ⚠️
                    </span>{' '}
                    Customers without name → "Customer"
                  </div>
                )}
              </div>
            )}
          </Card>
          {/* Footer toggle removed — the STOP opt-out line is always enabled
              (see `footer` constant) so customers can always unsubscribe. */}
          <Card style={{ marginBottom: 16 }}>
            <CLbl>Preview</CLbl>
            <MsgP
              g={greeting}
              gT={gText}
              msg="🎰 Double points this weekend!"
              photos={[]}
              name="María"
              footer={footer}
              locationName={LOC}
            />
          </Card>
          <button
            onClick={saveOwnerSettings}
            style={{
              width: '100%',
              padding: 13,
              background: gradG,
              border: 'none',
              borderRadius: 12,
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              marginBottom: 16,
            }}
          >
            Save Settings
          </button>

          {/* Platform Owner Admin Panel — only for OWNER / TECH_ADMIN */}
          {isPlatformOwner && (
            <>
              <div
                style={{
                  marginTop: 20,
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 5,
                    background: 'linear-gradient(135deg,#e5443a,#f97316)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ fontSize: 8, fontWeight: 800, color: '#fff' }}>
                    ★
                  </span>
                </div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.softTextMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  Platform Owner Only — NuVe System (ROLE_OWNER)
                </span>
              </div>
              <Card
                style={{
                  marginBottom: 16,
                  border: '1.5px solid rgba(229,68,58,0.2)',
                }}
              >
                <CLbl>Credit Management</CLbl>
                {/* Account Type */}
                <div style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.textMuted,
                      marginBottom: 6,
                    }}
                  >
                    Account Type
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[
                      { k: 'alpha', l: '⭐ Alpha' },
                      { k: 'global', l: '🌐 Global' },
                    ].map((o) => (
                      <button
                        key={o.k}
                        onClick={() => {
                          setAcctType(o.k);
                          if (o.k === 'global') setChargeMarkup(true);
                        }}
                        style={{
                          flex: 1,
                          padding: '10px 8px',
                          background:
                            acctType === o.k
                              ? o.k === 'alpha'
                                ? 'rgba(234,179,8,0.12)'
                                : 'rgba(91,76,219,0.1)'
                              : C.inputBg,
                          border: `1.5px solid ${
                            acctType === o.k
                              ? o.k === 'alpha'
                                ? '#eab308'
                                : C.accent
                              : C.border
                          }`,
                          borderRadius: 10,
                          cursor: 'pointer',
                          textAlign: 'center',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color:
                              acctType === o.k
                                ? o.k === 'alpha'
                                  ? '#a16207'
                                  : C.accent
                                : C.textMuted,
                          }}
                        >
                          {o.l}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.textMuted,
                      marginBottom: 6,
                    }}
                  >
                    Credit Limit
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {CREDIT_LIMIT_OPTIONS.map((v) => (
                      <button
                        key={v}
                        onClick={() => setAdminCreditLimit(v)}
                        style={{
                          flex: 1,
                          padding: '10px 4px',
                          background:
                            adminCreditLimit === v ? C.accent : C.inputBg,
                          border: `1.5px solid ${
                            adminCreditLimit === v ? C.accent : C.border
                          }`,
                          borderRadius: 8,
                          color: adminCreditLimit === v ? '#fff' : C.text,
                          fontSize: 14,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        ${v}
                      </button>
                    ))}
                  </div>
                </div>
                {acctType === 'alpha' && (
                  <div style={{ marginBottom: 14 }}>
                    <button
                      onClick={() => setChargeMarkup(!chargeMarkup)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: chargeMarkup ? C.accentPale : C.inputBg,
                        border: `1.5px solid ${
                          chargeMarkup ? C.accent : 'transparent'
                        }`,
                        borderRadius: 10,
                        cursor: 'pointer',
                        textAlign: 'left',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 40,
                          height: 24,
                          borderRadius: 12,
                          padding: 2,
                          background: chargeMarkup ? C.accent : C.border,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: chargeMarkup
                            ? 'flex-end'
                            : 'flex-start',
                        }}
                      >
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: C.white,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: C.text,
                          }}
                        >
                          Charge Markup
                        </div>
                        <div style={{ fontSize: 10, color: C.textMuted }}>
                          {chargeMarkup
                            ? 'Markup applied to this location'
                            : 'No markup — Twilio cost only'}
                        </div>
                      </div>
                    </button>
                  </div>
                )}
                {chargeMarkup || acctType === 'global' ? (
                  <div style={{ marginBottom: 14 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.textMuted,
                        marginBottom: 8,
                      }}
                    >
                      NuVe Markup (per message)
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        background: C.inputBg,
                        borderRadius: 8,
                        padding: 2,
                        marginBottom: 10,
                      }}
                    >
                      {[
                        { k: 'fixed', l: 'Fixed ¢' },
                        { k: 'pct', l: 'Percentage %' },
                      ].map((o) => (
                        <button
                          key={o.k}
                          onClick={() => setMarkupType(o.k)}
                          style={{
                            flex: 1,
                            padding: '8px 6px',
                            background:
                              markupType === o.k ? C.accent : 'transparent',
                            border: 'none',
                            borderRadius: 6,
                            color: markupType === o.k ? '#fff' : C.textMuted,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {o.l}
                        </button>
                      ))}
                    </div>
                    {markupType === 'fixed' ? (
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        {MARKUP_FIXED_OPTIONS.map((v) => {
                          const cents = Math.round(v * 100);
                          const isHalf = Math.abs(v * 1000 - cents * 10) >= 1;
                          const label =
                            v === 0
                              ? '$0'
                              : isHalf
                              ? `+${(v * 100).toFixed(1)}¢`
                              : `+${cents}¢`;
                          return (
                            <button
                              key={v}
                              onClick={() => setMarkup(v)}
                              style={{
                                flex: 1,
                                padding: '10px 4px',
                                background: markup === v ? C.accent : C.inputBg,
                                border: `1.5px solid ${
                                  markup === v ? C.accent : C.border
                                }`,
                                borderRadius: 8,
                                color: markup === v ? '#fff' : C.text,
                                fontSize: 13,
                                fontWeight: 700,
                                cursor: 'pointer',
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        {MARKUP_PCT_OPTIONS.map((v) => (
                          <button
                            key={v}
                            onClick={() => setMarkupPct(v)}
                            style={{
                              flex: 1,
                              padding: '10px 4px',
                              background:
                                markupPct === v ? C.accent : C.inputBg,
                              border: `1.5px solid ${
                                markupPct === v ? C.accent : C.border
                              }`,
                              borderRadius: 8,
                              color: markupPct === v ? '#fff' : C.text,
                              fontSize: 14,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {v}%
                          </button>
                        ))}
                      </div>
                    )}
                    <div
                      style={{
                        marginTop: 8,
                        padding: 10,
                        background: C.accentPale,
                        borderRadius: 8,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: 4,
                        }}
                      >
                        <span style={{ fontSize: 11, color: C.textSec }}>
                          SMS
                        </span>
                        <span style={{ fontSize: 11, color: C.textSec }}>
                          $
                          {(
                            SMS_BASE +
                            (markupType === 'fixed'
                              ? markup
                              : (SMS_BASE * markupPct) / 100)
                          ).toFixed(3)}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span style={{ fontSize: 11, color: C.textSec }}>
                          MMS
                        </span>
                        <span style={{ fontSize: 11, color: C.textSec }}>
                          $
                          {(
                            MMS_BASE +
                            (markupType === 'fixed'
                              ? markup
                              : (MMS_BASE * markupPct) / 100)
                          ).toFixed(3)}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      marginBottom: 14,
                      padding: '12px 14px',
                      background: C.greenPale,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <I d={ic.check} size={16} color={C.green} />
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: C.green,
                        }}
                      >
                        No Markup
                      </div>
                      <div style={{ fontSize: 10, color: C.textMuted }}>
                        SMS: $0.012 · MMS: $0.032 (Twilio cost only)
                      </div>
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: 14 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.textMuted,
                      marginBottom: 6,
                    }}
                  >
                    Low Balance Warning
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {LOW_THRESHOLD_OPTIONS.map((v) => (
                      <button
                        key={v}
                        onClick={() => setAdminLowThreshold(v)}
                        style={{
                          flex: 1,
                          padding: '10px 4px',
                          background:
                            adminLowThreshold === v ? C.orange : C.inputBg,
                          border: `1.5px solid ${
                            adminLowThreshold === v ? C.orange : C.border
                          }`,
                          borderRadius: 8,
                          color: adminLowThreshold === v ? '#fff' : C.text,
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        ${v}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => {
                    saveBoostAdmin({
                      locationId,
                      accountType: acctType,
                      chargeMarkup,
                      markupType,
                      markup,
                      markupPct,
                      creditLimit: adminCreditLimit,
                      lowThreshold: adminLowThreshold,
                    });
                    setAdminSaving(true);
                    setTimeout(() => setAdminSaving(false), 2000);
                    showFeedback('Admin settings saved');
                  }}
                  style={{
                    width: '100%',
                    padding: 13,
                    background: gradG,
                    border: 'none',
                    borderRadius: 10,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: 'pointer',
                    marginBottom: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <I d={ic.check} size={18} color="#fff" />
                  {adminSaving ? 'Settings Saved' : 'Save Changes'}
                </button>
                {/* Status panel */}
                <div
                  style={{
                    padding: 12,
                    background: C.inputBg,
                    borderRadius: 10,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.textMuted,
                      textTransform: 'uppercase',
                      marginBottom: 8,
                    }}
                  >
                    Current Status — {LOC}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 12, color: C.textSec }}>
                      Twilio Number
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: creditState?.fromNumber ? C.green : C.orange,
                      }}
                    >
                      {creditState?.fromNumber || 'Not provisioned yet'}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 12, color: C.textSec }}>
                      Credit Limit
                    </span>
                    <span
                      style={{ fontSize: 12, fontWeight: 600, color: C.text }}
                    >
                      ${creditLimit.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 12, color: C.textSec }}>
                      Phone # (monthly)
                    </span>
                    <span
                      style={{ fontSize: 12, fontWeight: 600, color: C.text }}
                    >
                      ${PHONE_FEE.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 12, color: C.textSec }}>
                      Total Used
                    </span>
                    <span
                      style={{ fontSize: 12, fontWeight: 600, color: C.red }}
                    >
                      ${creditUsed.toFixed(2)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.textSec,
                      }}
                    >
                      Remaining
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: creditEmpty
                          ? C.red
                          : creditLow
                          ? C.orange
                          : C.green,
                      }}
                    >
                      ${creditRemaining.toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {!confirmReset ? (
                      <button
                        onClick={() => setConfirmReset(true)}
                        style={{
                          flex: 1,
                          padding: 10,
                          background: gradG,
                          border: 'none',
                          borderRadius: 8,
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Reset Balance (Paid)
                      </button>
                    ) : (
                      <div
                        style={{
                          flex: 1,
                          background: C.greenPale,
                          borderRadius: 8,
                          padding: 8,
                          textAlign: 'center',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: C.text,
                            marginBottom: 6,
                          }}
                        >
                          Reset balance?
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => {
                              resetBoostBalance({ locationId });
                              setConfirmReset(false);
                            }}
                            style={{
                              flex: 1,
                              padding: 8,
                              background: gradG,
                              border: 'none',
                              borderRadius: 6,
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Yes, Reset
                          </button>
                          <button
                            onClick={() => setConfirmReset(false)}
                            style={{
                              flex: 1,
                              padding: 8,
                              background: C.white,
                              border: `1px solid ${C.border}`,
                              borderRadius: 6,
                              color: C.textMuted,
                              fontSize: 11,
                              cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {!confirmPause ? (
                      <button
                        onClick={() => setConfirmPause(true)}
                        style={{
                          flex: 1,
                          padding: 10,
                          background: C.redPale,
                          border: `1.5px solid ${C.red}`,
                          borderRadius: 8,
                          color: C.red,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Pause
                      </button>
                    ) : (
                      <div
                        style={{
                          flex: 1,
                          background: C.redPale,
                          borderRadius: 8,
                          padding: 8,
                          textAlign: 'center',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: C.red,
                            marginBottom: 6,
                          }}
                        >
                          Pause messaging?
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => {
                              pauseBoost({ locationId });
                              setConfirmPause(false);
                            }}
                            style={{
                              flex: 1,
                              padding: 8,
                              background: C.red,
                              border: 'none',
                              borderRadius: 6,
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Yes, Pause
                          </button>
                          <button
                            onClick={() => setConfirmPause(false)}
                            style={{
                              flex: 1,
                              padding: 8,
                              background: C.white,
                              border: `1px solid ${C.border}`,
                              borderRadius: 6,
                              color: C.textMuted,
                              fontSize: 11,
                              cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </Shell>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // SCREEN: Templates
  // ══════════════════════════════════════════════════════════════════════
  if (screen === 'templates') {
    return (
      <Shell pageName="Saved Templates" onBack={() => setScreen('home')}>
        <div style={{ padding: 20 }}>
          {templates.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <I d={ic.bookmark} size={40} color="#d8dbe8" />
              <p
                style={{ color: C.softTextMuted, fontSize: 14, marginTop: 12 }}
              >
                No templates saved yet
              </p>
            </div>
          )}
          {templates.map((t) => (
            <Card key={t.id} style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
                  {t.name}
                </div>
                <button
                  onClick={() => delTpl(t.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                  }}
                >
                  <I d={ic.trash} size={16} color={C.red} />
                </button>
              </div>
              <p
                style={{
                  margin: '0 0 10px',
                  fontSize: 13,
                  color: C.textMuted,
                  lineHeight: 1.4,
                }}
              >
                {(t.msg || '').slice(0, 80)}
                {(t.msg || '').length > 80 ? '...' : ''}
              </p>
              <button
                onClick={() => loadTpl(t)}
                style={{
                  width: '100%',
                  padding: 10,
                  background: grad,
                  border: 'none',
                  borderRadius: 10,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Use This Template
              </button>
            </Card>
          ))}
        </div>
      </Shell>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // SCREEN: Analytics + Report
  // ══════════════════════════════════════════════════════════════════════
  if (screen === 'analytics' || screen === 'report') {
    return (
      <AnalyticsScreen
        isReport={screen === 'report'}
        onBack={() => setScreen('home')}
        onToReport={() => setScreen('report')}
        onToAnalytics={() => setScreen('analytics')}
        hist={hist}
        totalOptOuts={totalOptOuts}
        LOC={LOC}
        isDesktop={isDesktop}
      />
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // SCREEN: Quick Send
  // ══════════════════════════════════════════════════════════════════════
  if (screen === 'quick') {
    const recent = hist.filter((h) => h.status === 'delivered').slice(0, 10);
    const qPick = (h) => {
      reset();
      setMsg(h.msg || '');
      setITitle(h.iTitle || '');
      setGreeting(h.greeting || 'none');
      setGText(h.gText || 'Hello!');
      setAudType('all');
      setScreen('compose');
      setStep(2);
    };
    const qTpl = (t) => {
      reset();
      setMsg(t.msg || '');
      setITitle(t.name || '');
      setGreeting(t.greeting || 'none');
      setGText(t.gText || 'Hello!');
      setAudType('all');
      setScreen('compose');
      setStep(2);
    };
    return (
      <Shell pageName="Quick Send" onBack={() => setScreen('home')}>
        <div style={{ padding: 20 }}>
          <div
            style={{
              padding: '10px 14px',
              background: C.softBg,
              borderRadius: 10,
              marginBottom: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <I d={ic.zap} size={16} color={C.orange} />
            <span style={{ fontSize: 12, color: C.softText }}>
              Tap a message →{' '}
              <strong style={{ color: C.text }}>All Customers</strong> → Review
            </span>
          </div>
          <TabBar
            tabs={[
              {
                k: 'recent',
                l: 'Last Sent',
                ico: ic.send,
                badge: recent.length > 0 ? recent.length : null,
                badgeColor: C.green,
              },
              {
                k: 'templates',
                l: 'Templates',
                ico: ic.bookmark,
                badge: templates.length > 0 ? templates.length : null,
                badgeColor: C.accentLight,
              },
            ]}
            active={quickTab}
            onChange={setQuickTab}
          />
          {quickTab === 'recent' &&
            (recent.length > 0 ? (
              recent.map((h) => (
                <button
                  key={h.id}
                  onClick={() => qPick(h)}
                  style={{
                    width: '100%',
                    marginBottom: 8,
                    padding: '12px 14px',
                    background: C.white,
                    border: 'none',
                    borderRadius: 12,
                    cursor: 'pointer',
                    textAlign: 'left',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: h.img
                        ? `linear-gradient(135deg,${C.hStart},${C.hEnd})`
                        : C.greenPale,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <I
                      d={h.img ? ic.image : ic.send}
                      size={16}
                      color={h.img ? '#fff' : C.green}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{ fontSize: 13, fontWeight: 600, color: C.text }}
                    >
                      {h.iTitle}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>
                      {h.date} · {h.n} sent
                    </div>
                  </div>
                  <I d={ic.chevR} size={16} color={C.textDim} />
                </button>
              ))
            ) : (
              <EmptyHint icon={ic.send} text="No messages sent yet" />
            ))}
          {quickTab === 'templates' &&
            (templates.length > 0 ? (
              templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => qTpl(t)}
                  style={{
                    width: '100%',
                    marginBottom: 8,
                    padding: '12px 14px',
                    background: C.white,
                    border: 'none',
                    borderRadius: 12,
                    cursor: 'pointer',
                    textAlign: 'left',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: C.accentPale,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <I d={ic.bookmark} size={16} color={C.accent} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{ fontSize: 13, fontWeight: 600, color: C.text }}
                    >
                      {t.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.textMuted,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {(t.msg || '').slice(0, 60)}
                      {(t.msg || '').length > 60 ? '...' : ''}
                    </div>
                  </div>
                  <I d={ic.chevR} size={16} color={C.textDim} />
                </button>
              ))
            ) : (
              <EmptyHint icon={ic.bookmark} text="No templates saved yet" />
            ))}
          <div
            style={{
              marginTop: 16,
              borderTop: `1px solid ${C.border}`,
              paddingTop: 16,
            }}
          >
            <button
              onClick={() => {
                reset();
                setAudType('all');
                setScreen('compose');
                setStep(0);
              }}
              style={{
                width: '100%',
                padding: 13,
                background: C.white,
                border: '1.5px solid #d8dbe8',
                borderRadius: 12,
                color: C.accent,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <I d={ic.edit} size={16} color={C.accent} />
              Write New to All
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // SCREEN: Sent (success)
  // ══════════════════════════════════════════════════════════════════════
  if (screen === 'sent') {
    const lastSent = hist[0];
    return (
      <Shell>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '50px 30px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: gradG,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 20,
              boxShadow: `0 10px 40px ${C.greenGlow}`,
            }}
          >
            <I d={ic.check} size={40} color="#fff" />
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: C.text,
              marginBottom: 6,
            }}
          >
            {sendMode === 'schedule' ? 'Scheduled!' : 'Sent!'}
          </div>
          <div style={{ fontSize: 14, color: C.softText, marginBottom: 20 }}>
            {sendMode === 'schedule'
              ? 'Your message has been scheduled.'
              : 'Your message was sent successfully.'}
          </div>
          {lastSent && (
            <div
              style={{
                width: '100%',
                padding: 14,
                background: C.softBg,
                borderRadius: 12,
                marginBottom: 24,
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.text,
                  marginBottom: 6,
                }}
              >
                {lastSent.iTitle}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 3,
                }}
              >
                <span style={{ fontSize: 11, color: C.softTextMuted }}>
                  Recipients
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>
                  {lastSent.n}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 3,
                }}
              >
                <span style={{ fontSize: 11, color: C.softTextMuted }}>
                  Cost
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.accentLight,
                  }}
                >
                  ${(lastSent.cost || 0).toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, color: C.softTextMuted }}>
                  Credit Remaining
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: creditRemaining > lowThreshold ? C.green : C.orange,
                  }}
                >
                  ${creditRemaining.toFixed(2)}
                </span>
              </div>
            </div>
          )}
          <button
            onClick={() => {
              reset();
              setScreen('home');
            }}
            style={{
              width: '100%',
              padding: 14,
              background: grad,
              border: 'none',
              borderRadius: 14,
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <I d={ic.mega} size={18} color="#fff" />
            Back to NuVe Boost
          </button>
        </div>
      </Shell>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // SCREEN: Compose — Steps 0, 1, 2
  // ══════════════════════════════════════════════════════════════════════
  if (screen === 'compose' && step === 0) {
    return (
      <Shell pageName="New Message" onBack={() => setScreen('home')}>
        <Steps cur={0} tot={3} />
        <div style={{ padding: '8px 20px 20px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 14,
              padding: '7px 12px',
              background: C.softBg,
              borderRadius: 10,
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: C.green,
              }}
            />
            <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
              {LOC}
            </span>
            {greeting !== 'none' && (
              <>
                <span style={{ color: C.softTextDim }}>·</span>
                <span style={{ fontSize: 12, color: C.softText }}>
                  {greeting === 'generic'
                    ? gText
                    : `${gText.replace(/!$/, '')} [Name]!`}
                </span>
              </>
            )}
          </div>
          <div style={{ marginBottom: 14 }}>
            <Lbl>
              Title{' '}
              <span
                style={{
                  fontWeight: 400,
                  textTransform: 'none',
                  color: C.softTextMuted,
                }}
              >
                (internal)
              </span>
            </Lbl>
            <input
              value={iTitle}
              onChange={(e) => setITitle(e.target.value)}
              placeholder="e.g. Weekend Promo April"
              maxLength={60}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: C.white,
                border: `1.5px solid ${C.border}`,
                borderRadius: 10,
                color: C.text,
                fontSize: 14,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <Lbl>Message</Lbl>
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Write your message..."
              maxLength={msgLimit}
              style={{
                width: '100%',
                minHeight: 100,
                background: C.white,
                border: `1.5px solid ${C.border}`,
                borderRadius: 14,
                padding: 14,
                color: C.text,
                fontSize: 15,
                lineHeight: 1.5,
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 4,
              }}
            >
              {photos.length === 0 && msg.length > 0 ? (
                <span
                  style={{
                    fontSize: 11,
                    color: segs.s > 1 ? C.orange : C.softTextMuted,
                  }}
                >
                  {segs.s} SMS segment{segs.s !== 1 ? 's' : ''}
                </span>
              ) : (
                <span />
              )}
              <span
                style={{
                  fontSize: 12,
                  color: msgUnits > msgLimit - 20 ? C.orange : C.softTextMuted,
                }}
              >
                {msgUnits}/{msgLimit}
              </span>
            </div>
            {segs.uni && msg.length > 0 && (
              <div
                style={{
                  marginTop: 6,
                  padding: '8px 12px',
                  background: C.orangePale,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                }}
              >
                <I d={ic.alert} size={14} color={C.orange} />
                <div style={{ fontSize: 11, color: C.orange, lineHeight: 1.4 }}>
                  Emoji/special characters detected (
                  <strong>{segs.bad.slice(0, 6).join(' ')}</strong>
                  {segs.bad.length > 6 ? '…' : ''}) — limit reduced from 160 to{' '}
                  <strong>70 characters</strong> per segment.
                  {!segs.bad.some((ch) => msg.includes(ch)) &&
                    ' They come from the location name or greeting line, not your message.'}
                </div>
              </div>
            )}
          </div>
          {ok0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 14,
                padding: '5px 12px',
                background: 'rgba(91,76,219,0.1)',
                borderRadius: 8,
              }}
            >
              <I d={ic.info} size={12} color={C.accentLight} />
              <span style={{ fontSize: 11, color: C.accentLight }}>
                ~${costPer().toFixed(3)}/recipient (
                {photos.length > 0 ? 'MMS' : 'SMS'})
              </span>
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <Lbl style={{ marginBottom: 10 }}>
              Photos{' '}
              <span
                style={{
                  fontWeight: 400,
                  textTransform: 'none',
                  color: C.softTextMuted,
                }}
              >
                (optional)
              </span>
            </Lbl>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFile}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {photos.map((p) => (
                <div key={p.id} style={{ position: 'relative' }}>
                  <AttachmentThumb src={p.url} name={p.name} />
                  <button
                    onClick={() =>
                      setPhotos(photos.filter((x) => x.id !== p.id))
                    }
                    style={{
                      position: 'absolute',
                      top: -5,
                      right: -5,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: C.red,
                      border: `2px solid ${C.bg}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    <I d={ic.x} size={10} color="#fff" />
                  </button>
                </div>
              ))}
              {photos.length < 4 && (
                <button
                  onClick={() => fileRef.current?.click()}
                  style={{
                    width: 'clamp(64px, 20vw, 88px)',
                    aspectRatio: '1 / 1',
                    borderRadius: 12,
                    background: C.softBg,
                    border: '2px dashed #d8dbe8',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 3,
                    cursor: 'pointer',
                    color: C.softText,
                  }}
                >
                  <I d={ic.upload} size={18} color={C.textMuted} />
                  <span style={{ fontSize: 9 }}>Upload</span>
                </button>
              )}
            </div>
            {photoErrs.length > 0 && (
              <div
                style={{
                  marginTop: 6,
                  padding: '5px 10px',
                  background: C.redPale,
                  borderRadius: 8,
                  fontSize: 11,
                  color: C.red,
                }}
              >
                {photoErrs.join('. ')}
              </div>
            )}
          </div>
          {ok0 && (
            <div style={{ marginBottom: 14 }}>
              <Lbl style={{ marginBottom: 10 }}>Preview</Lbl>
              <MsgP
                g={greeting}
                gT={gText}
                msg={msg}
                photos={photos}
                footer={footer}
                name="María"
                locationName={LOC}
              />
            </div>
          )}
          {ok0 && (
            <div style={{ marginBottom: 14 }}>
              {!showSaveTpl ? (
                <button
                  onClick={() => setShowSaveTpl(true)}
                  style={{
                    width: '100%',
                    padding: 10,
                    background: 'transparent',
                    border: '1px solid #eef0f6',
                    borderRadius: 10,
                    color: C.softTextMuted,
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  <I d={ic.bookmark} size={14} color={C.textMuted} /> Save as
                  Template
                </button>
              ) : (
                <div
                  style={{
                    padding: 12,
                    background: C.softBg,
                    borderRadius: 12,
                  }}
                >
                  <input
                    value={saveTplName}
                    onChange={(e) => setSaveTplName(e.target.value)}
                    placeholder="Template name..."
                    maxLength={40}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: C.white,
                      border: `1.5px solid ${C.accent}`,
                      borderRadius: 8,
                      color: C.text,
                      fontSize: 13,
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      outline: 'none',
                      marginBottom: 8,
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={saveTplAct}
                      disabled={!saveTplName.trim()}
                      style={{
                        flex: 1,
                        padding: 10,
                        background: saveTplName.trim() ? C.accent : C.border,
                        border: 'none',
                        borderRadius: 8,
                        color: saveTplName.trim() ? '#fff' : disTxt,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: saveTplName.trim() ? 'pointer' : 'default',
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setShowSaveTpl(false);
                        setSaveTplName('');
                      }}
                      style={{
                        padding: 10,
                        background: 'none',
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        color: C.textMuted,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <button
            onClick={() => setStep(1)}
            disabled={!ok0}
            style={{
              width: '100%',
              padding: 14,
              background: ok0 ? grad : dis,
              border: 'none',
              borderRadius: 14,
              color: ok0 ? '#fff' : disTxt,
              fontSize: 16,
              fontWeight: 600,
              cursor: ok0 ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            Next — Audience{' '}
            <I d={ic.chevR} size={18} color={ok0 ? '#fff' : disTxt} />
          </button>
        </div>
      </Shell>
    );
  }

  if (screen === 'compose' && step === 1) {
    const filtered = CUST.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.phone || '').includes(search)
    );
    const totalRec = getRec().length;
    return (
      <Shell pageName="Select Audience" onBack={() => setStep(0)}>
        <Steps cur={1} tot={3} />
        {totalRec > 0 && (
          <div style={{ padding: '4px 20px 0' }}>
            <div
              style={{
                padding: '10px 16px',
                background: grad,
                borderRadius: 12,
                boxShadow: '0 6px 18px rgba(91,76,219,0.25)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <I d={ic.users} size={15} color="#fff" />
                  <span
                    style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}
                  >
                    {audCnt()} recipients
                  </span>
                  {getInvalid().length > 0 && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: '#fff',
                        background: 'rgba(255,255,255,0.18)',
                        padding: '2px 8px',
                        borderRadius: 8,
                      }}
                    >
                      {getInvalid().length} invalid
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>
                  ~${estCost()}
                </span>
              </div>
              {audType !== 'all' && (
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.75)',
                    marginTop: 3,
                  }}
                >
                  {audLbl()}
                </div>
              )}
            </div>
          </div>
        )}
        <div style={{ padding: '12px 20px 20px' }}>
          {/* Only phone-verified customers (who completed OTP + consent) are
              messageable, so every option below is drawn from that set. */}
          <div
            style={{
              fontSize: 11,
              color: C.textMuted,
              margin: '0 0 10px',
              lineHeight: 1.4,
            }}
          >
            Only <strong style={{ color: C.text }}>phone-verified</strong>{' '}
            customers (verified &amp; consented) can be messaged — unverified
            customers aren’t shown.
          </div>
          {/* All Customers */}
          <button
            onClick={() => {
              if (audType === 'all') {
                setAudType(null);
              } else {
                setAudType('all');
                setSelMatch([]);
                setVipDes([]);
                setSelCust([]);
                setSelInactive([]);
                setInactiveDays(null);
                setAudOpen('');
              }
            }}
            style={{
              width: '100%',
              padding: '14px 16px',
              marginBottom: 6,
              background: audType === 'all' ? C.accentPale : C.white,
              border: `1.5px solid ${audType === 'all' ? C.accent : C.border}`,
              borderRadius: 14,
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              boxShadow:
                audType === 'all'
                  ? '0 4px 14px rgba(91,76,219,0.15)'
                  : '0 1px 4px rgba(0,0,0,0.04)',
              transition: 'all 0.15s ease',
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: audType === 'all' ? grad : C.softBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <I
                d={ic.users}
                size={18}
                color={audType === 'all' ? '#fff' : C.softTextMuted}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: audType === 'all' ? C.accent : C.text,
                }}
              >
                All Active Customers
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: audType === 'all' ? C.textSec : C.textMuted,
                  marginTop: 1,
                }}
              >
                {validCust.length} phone-verified
                {BLACKED.length > 0 ? ` (${BLACKED.length} blacklisted)` : ''}
              </div>
            </div>
            {audType === 'all' ? (
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: C.accent,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <I d={ic.check} size={12} color="#fff" />
              </div>
            ) : (
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: '2px solid #d8dbe8',
                  flexShrink: 0,
                }}
              />
            )}
          </button>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              margin: '10px 0',
            }}
          >
            <div style={{ flex: 1, height: 1, background: C.borderLight }} />
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.softTextDim,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {audType === 'all' ? 'or build custom' : 'Build Your Audience'}
            </span>
            <div style={{ flex: 1, height: 1, background: C.borderLight }} />
          </div>
          {/* VIP */}
          <AudienceAccordion
            label="VIP by Match"
            badge={vipFinal.length}
            badgeColor={C.orange}
            badgeBg={C.orangePale}
            iconNode={
              <Star
                size={16}
                color={vipFinal.length > 0 ? C.orange : C.softTextDim}
              />
            }
            open={audOpen === 'vip'}
            disabled={audType === 'all'}
            onToggle={() => {
              if (audType === 'all') setAudType(null);
              setAudOpen((p) => (p === 'vip' ? '' : 'vip'));
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Match Values
              </span>
              <button
                onClick={togAll}
                style={{
                  background: C.accentPale,
                  border: 'none',
                  borderRadius: 8,
                  padding: '4px 10px',
                  color: C.accent,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {selMatch.length === allMatch.length
                  ? 'Deselect All'
                  : 'Select All'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {allMatch.map((v) => {
                const cnt = CUST.filter((c) => c.match === v).length;
                const sel = selMatch.includes(v);
                return (
                  <button
                    key={v}
                    onClick={() => togM(v)}
                    style={{
                      padding: '7px 12px',
                      background: sel ? C.orangePale : C.white,
                      border: `1.5px solid ${sel ? C.orange : C.border}`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      transition: 'all 0.12s ease',
                    }}
                  >
                    <Star size={11} color={sel ? C.orange : C.textDim} />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: sel ? C.orange : C.textSec,
                      }}
                    >
                      {v}+
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: sel ? C.orange : C.textDim,
                      }}
                    >
                      {cnt}
                    </span>
                  </button>
                );
              })}
            </div>
            {vipFinal.length > 0 && (
              <div style={{ marginTop: 12, maxHeight: 170, overflowY: 'auto' }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: C.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: 6,
                  }}
                >
                  {vipFinal.length} selected — tap × to exclude
                </div>
                {vipFinal.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '7px 0',
                      borderBottom: `1px solid ${C.borderLight}`,
                    }}
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <div
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: '50%',
                          background: C.orangePale,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 9,
                          fontWeight: 700,
                          color: C.orange,
                          flexShrink: 0,
                        }}
                      >
                        {c.av}
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: C.text,
                        }}
                      >
                        {c.name || 'Unknown'}
                      </span>
                    </div>
                    <button
                      onClick={() => setVipDes((p) => [...p, c.id])}
                      style={{
                        width: 22,
                        height: 22,
                        background: C.redPale,
                        border: 'none',
                        borderRadius: '50%',
                        fontSize: 12,
                        lineHeight: 1,
                        color: C.red,
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {vipDes.length > 0 && (
                  <button
                    onClick={() => setVipDes([])}
                    style={{
                      marginTop: 8,
                      background: 'none',
                      border: 'none',
                      color: C.accent,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    ↺ Restore {vipDes.length} excluded
                  </button>
                )}
              </div>
            )}
          </AudienceAccordion>
          {/* Inactive / Dormant */}
          <AudienceAccordion
            label="Inactive / Dormant"
            badge={selInactive.length}
            badgeColor={C.accent}
            badgeBg={C.accentPale}
            iconNode={
              <I
                d={ic.cal}
                size={16}
                color={selInactive.length > 0 ? C.accent : C.softTextDim}
              />
            }
            open={audOpen === 'inactive'}
            disabled={audType === 'all'}
            onToggle={() => {
              if (audType === 'all') setAudType(null);
              setAudOpen((p) => (p === 'inactive' ? '' : 'inactive'));
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: C.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 8,
              }}
            >
              Not visited in
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[30, 60, 90].map((day) => {
                const cnt = inactiveMatch(day).length;
                const sel = inactiveDays === day;
                return (
                  <button
                    key={day}
                    onClick={() => pickInactive(day)}
                    style={{
                      padding: '7px 12px',
                      background: sel ? C.accentPale : C.white,
                      border: `1.5px solid ${sel ? C.accent : C.border}`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      transition: 'all 0.12s ease',
                    }}
                  >
                    <I
                      d={ic.cal}
                      size={11}
                      color={sel ? C.accent : C.textDim}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: sel ? C.accent : C.textSec,
                      }}
                    >
                      {day}+ days
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: sel ? C.accent : C.textDim,
                      }}
                    >
                      {cnt}
                    </span>
                  </button>
                );
              })}
            </div>
            <div
              style={{
                fontSize: 10.5,
                color: C.textMuted,
                marginTop: 8,
                lineHeight: 1.4,
              }}
            >
              Selects every messageable customer who hasn’t visited in that many
              days — a win-back blast to dormant members.
            </div>
            {inactiveDays && (
              <div style={{ marginTop: 12, maxHeight: 170, overflowY: 'auto' }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: C.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: 6,
                  }}
                >
                  {inactiveMatch(inactiveDays).length} selected
                </div>
                {inactiveMatch(inactiveDays).map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '7px 2px',
                      borderBottom: `1px solid ${C.borderLight}`,
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        background: C.accentPale,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.accent,
                        flexShrink: 0,
                      }}
                    >
                      {c.av}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: C.text,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {c.name || 'Unknown'}
                        {!c.phoneValid && (
                          <span style={{ color: C.red, fontWeight: 700 }}>
                            {' '}
                            · invalid #
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: C.textMuted }}>
                        {c.phone || '—'}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.accent,
                        background: C.accentPale,
                        padding: '3px 8px',
                        borderRadius: 8,
                        flexShrink: 0,
                      }}
                    >
                      {c.lastDays}d
                    </span>
                  </div>
                ))}
              </div>
            )}
          </AudienceAccordion>
          {/* Individual */}
          <AudienceAccordion
            label="Select Individual"
            badge={selCust.length}
            badgeColor={C.green}
            badgeBg={C.greenPale}
            iconNode={
              <I
                d={ic.check}
                size={16}
                color={selCust.length > 0 ? C.green : C.softTextDim}
              />
            }
            open={audOpen === 'sel'}
            disabled={audType === 'all'}
            onToggle={() => {
              if (audType === 'all') setAudType(null);
              setAudOpen((p) => (p === 'sel' ? '' : 'sel'));
            }}
          >
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or phone…"
              style={{
                width: '100%',
                padding: '10px 14px',
                background: C.inputBg,
                border: `1.5px solid ${C.border}`,
                borderRadius: 10,
                color: C.text,
                fontSize: 13,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                outline: 'none',
                marginBottom: 10,
              }}
            />
            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
              {filtered.map((c) => {
                const sel = selCust.includes(c.id);
                return (
                  <div
                    key={c.id}
                    onClick={() =>
                      setSelCust((p) =>
                        sel ? p.filter((x) => x !== c.id) : [...p, c.id]
                      )
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '8px 2px',
                      borderBottom: `1px solid ${C.borderLight}`,
                      cursor: 'pointer',
                      borderRadius: 6,
                      background: sel ? `${C.green}08` : 'transparent',
                      transition: 'background 0.12s ease',
                    }}
                  >
                    <div
                      style={{
                        width: 19,
                        height: 19,
                        borderRadius: 6,
                        border: `2px solid ${sel ? C.green : '#d8dbe8'}`,
                        background: sel ? C.green : C.white,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.12s ease',
                      }}
                    >
                      {sel && <I d={ic.check} size={9} color="#fff" />}
                    </div>
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: C.accentPale,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.accent,
                        flexShrink: 0,
                      }}
                    >
                      {c.av}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: C.text,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {c.name || 'Unknown'}
                      </div>
                      <div style={{ fontSize: 10, color: C.textMuted }}>
                        {c.phone}
                        {c.status === 'inactive' && (
                          <span
                            style={{
                              marginLeft: 5,
                              fontSize: 8.5,
                              fontWeight: 700,
                              color: C.purple,
                              background: C.purplePale,
                              padding: '1px 5px',
                              borderRadius: 4,
                              verticalAlign: 'middle',
                            }}
                          >
                            INACTIVE
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        fontSize: 11,
                        fontWeight: 600,
                        color: C.textMuted,
                        flexShrink: 0,
                      }}
                    >
                      <Star size={10} color={C.orange} />
                      {c.match}
                    </span>
                  </div>
                );
              })}
            </div>
          </AudienceAccordion>
          <button
            onClick={() => setStep(2)}
            disabled={!ok1}
            style={{
              width: '100%',
              padding: 14,
              background: ok1 ? grad : dis,
              border: 'none',
              borderRadius: 14,
              color: ok1 ? '#fff' : disTxt,
              fontSize: 16,
              fontWeight: 600,
              cursor: ok1 ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginTop: 12,
            }}
          >
            Next — Review{' '}
            <I d={ic.chevR} size={18} color={ok1 ? '#fff' : disTxt} />
          </button>
        </div>
      </Shell>
    );
  }

  if (screen === 'compose' && step === 2) {
    const v = getValid();
    const inv = getInvalid();
    const costF = (costPer() * v.length).toFixed(2);
    const canAfford = creditRemaining >= parseFloat(costF);
    return (
      <Shell pageName="Review & Send" onBack={() => setStep(1)}>
        <Steps cur={2} tot={3} />
        {sending ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '80px 30px',
            }}
          >
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                border: '4px solid #e8e9f0',
                borderTopColor: C.accentLight,
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <p style={{ color: C.softText, marginTop: 16, fontSize: 14 }}>
              {sendMode === 'schedule' ? 'Scheduling...' : 'Sending...'}
            </p>
          </div>
        ) : (
          <div style={{ padding: '12px 20px 20px' }}>
            {sendResult && !sendResult.ok && (
              <div
                style={{
                  marginBottom: 14,
                  padding: '12px 14px',
                  background: C.redPale,
                  border: `1.5px solid ${C.red}`,
                  borderRadius: 12,
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                }}
              >
                <I d={ic.alert} size={16} color={C.red} />
                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: C.red,
                      marginBottom: 2,
                    }}
                  >
                    Send failed
                  </div>
                  <div style={{ fontSize: 12, color: C.red, lineHeight: 1.45 }}>
                    {sendResult.message}
                  </div>
                </div>
              </div>
            )}
            <Card style={{ marginBottom: 14 }}>
              <CLbl>Message</CLbl>
              <MsgP
                g={greeting}
                gT={gText}
                msg={msg}
                photos={photos}
                footer={footer}
                name="María"
                locationName={LOC}
              />
            </Card>
            <Card style={{ marginBottom: 14 }}>
              <CLbl>Audience</CLbl>
              <div
                style={{
                  padding: '10px 14px',
                  background: C.inputBg,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <I d={ic.users} size={18} color={C.accent} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                    {audLbl()}
                  </div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>
                    {v.length} valid recipients
                    {inv.length > 0 ? ` · ${inv.length} invalid skipped` : ''}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setStep(1)}
                style={{
                  width: '100%',
                  marginTop: 10,
                  padding: 10,
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  color: C.accent,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Change Audience / Add More People
              </button>
            </Card>
            <Card style={{ marginBottom: 14 }}>
              <CLbl>Cost</CLbl>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: 13, color: C.textSec }}>Type</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                  {photos.length > 0 ? 'MMS' : 'SMS'}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: 13, color: C.textSec }}>
                  Recipients
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                  {v.length}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 0',
                  borderTop: `1.5px solid ${C.border}`,
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                  Estimated Cost
                </span>
                <span
                  style={{ fontSize: 18, fontWeight: 700, color: C.accent }}
                >
                  ~${costF}
                </span>
              </div>
              {!canAfford && (
                <div style={{ marginTop: 8 }}>
                  <div
                    style={{
                      padding: '8px 10px',
                      background: C.redPale,
                      borderRadius: 8,
                      fontSize: 12,
                      color: C.red,
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    Insufficient credit (${creditRemaining.toFixed(2)}{' '}
                    remaining)
                  </div>
                  <RechargeBtn />
                </div>
              )}
            </Card>
            <Card style={{ marginBottom: 14 }}>
              <CLbl>Delivery</CLbl>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {[
                  { k: 'now', l: 'Send Now', ic: ic.send },
                  { k: 'schedule', l: 'Schedule', ic: ic.cal },
                ].map((o) => (
                  <button
                    key={o.k}
                    onClick={() => setSendMode(o.k)}
                    style={{
                      flex: 1,
                      padding: '12px 8px',
                      background:
                        sendMode === o.k ? 'rgba(91,76,219,0.08)' : C.inputBg,
                      border: `1.5px solid ${
                        sendMode === o.k ? C.accent : C.border
                      }`,
                      borderRadius: 10,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                  >
                    <I
                      d={o.ic}
                      size={14}
                      color={sendMode === o.k ? C.accent : C.textMuted}
                    />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: sendMode === o.k ? C.accent : C.textMuted,
                      }}
                    >
                      {o.l}
                    </span>
                  </button>
                ))}
              </div>
              {sendMode === 'schedule' && (
                <div>
                  <div
                    style={{
                      background: C.inputBg,
                      borderRadius: 12,
                      padding: 12,
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 10,
                      }}
                    >
                      <button
                        onClick={calPrev}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                        }}
                      >
                        <I d={ic.chevL} size={18} color={C.textMuted} />
                      </button>
                      <span
                        style={{ fontSize: 14, fontWeight: 700, color: C.text }}
                      >
                        {mNames[calMonth]} {calYear}
                      </span>
                      <button
                        onClick={calNext}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                        }}
                      >
                        <I d={ic.chevR} size={18} color={C.textMuted} />
                      </button>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7,1fr)',
                        gap: 2,
                      }}
                    >
                      {dNames.map((d) => (
                        <div
                          key={d}
                          style={{
                            textAlign: 'center',
                            fontSize: 10,
                            fontWeight: 600,
                            color: C.textMuted,
                            padding: '4px 0',
                          }}
                        >
                          {d}
                        </div>
                      ))}
                      {calCells.map((d, i) => {
                        if (!d) return <div key={`e${i}`} />;
                        const ds = `${calYear}-${String(calMonth + 1).padStart(
                          2,
                          '0'
                        )}-${String(d).padStart(2, '0')}`;
                        const isSel = sDate === ds;
                        const isToday = ds === calTodayStr;
                        const isPast = new Date(ds) < new Date(calTodayStr);
                        return (
                          <button
                            key={i}
                            onClick={() => !isPast && calPick(d)}
                            style={{
                              padding: '8px 0',
                              background: isSel
                                ? C.accent
                                : isToday
                                ? 'rgba(91,76,219,0.1)'
                                : 'transparent',
                              border: 'none',
                              borderRadius: 8,
                              color: isSel
                                ? '#fff'
                                : isPast
                                ? C.textDim
                                : isToday
                                ? C.accent
                                : C.text,
                              fontSize: 13,
                              fontWeight: isSel || isToday ? 700 : 400,
                              cursor: isPast ? 'default' : 'pointer',
                              opacity: isPast ? 0.4 : 1,
                            }}
                          >
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div
                    style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: C.textMuted,
                          marginBottom: 4,
                        }}
                      >
                        Hour
                      </div>
                      <select
                        value={sHour}
                        onChange={(e) =>
                          calTime(parseInt(e.target.value, 10), sMin, sAmpm)
                        }
                        style={{
                          width: '100%',
                          padding: '10px 6px',
                          background: C.white,
                          border: `1.5px solid ${C.border}`,
                          borderRadius: 8,
                          color: C.text,
                          fontSize: 15,
                          fontWeight: 600,
                          textAlign: 'center',
                        }}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                          <option key={h} value={h}>
                            {h}
                          </option>
                        ))}
                      </select>
                    </div>
                    <span
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: C.textMuted,
                        marginTop: 16,
                      }}
                    >
                      :
                    </span>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: C.textMuted,
                          marginBottom: 4,
                        }}
                      >
                        Min
                      </div>
                      <select
                        value={sMin}
                        onChange={(e) =>
                          calTime(sHour, parseInt(e.target.value, 10), sAmpm)
                        }
                        style={{
                          width: '100%',
                          padding: '10px 6px',
                          background: C.white,
                          border: `1.5px solid ${C.border}`,
                          borderRadius: 8,
                          color: C.text,
                          fontSize: 15,
                          fontWeight: 600,
                          textAlign: 'center',
                        }}
                      >
                        {[0, 15, 30, 45].map((m) => (
                          <option key={m} value={m}>
                            {m < 10 ? `0${m}` : m}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: C.textMuted,
                          marginBottom: 4,
                        }}
                      >
                        &nbsp;
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          borderRadius: 8,
                          overflow: 'hidden',
                          border: `1.5px solid ${C.border}`,
                        }}
                      >
                        {['AM', 'PM'].map((ap) => (
                          <button
                            key={ap}
                            onClick={() => calTime(sHour, sMin, ap)}
                            style={{
                              flex: 1,
                              padding: '10px 4px',
                              background: sAmpm === ap ? C.accent : C.white,
                              border: 'none',
                              color: sAmpm === ap ? '#fff' : C.textMuted,
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            {ap}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {sendMode === 'schedule' && sDate && sTime && (
                <div
                  style={{
                    marginTop: 10,
                    padding: '10px 14px',
                    background: C.accentPale,
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.accent,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <I d={ic.cal} size={14} color={C.accent} />
                  {fmtSched()}
                </div>
              )}
            </Card>
            {cooldownActive && (
              <div
                style={{
                  marginBottom: 10,
                  padding: '8px 10px',
                  background: C.orangePale,
                  borderRadius: 8,
                  fontSize: 12,
                  color: C.orange,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <I d={ic.alert} size={14} color={C.orange} />
                Cooldown active — you can send again in{' '}
                {Math.floor(cooldownLeft / 60)}:
                {String(cooldownLeft % 60).padStart(2, '0')}.
              </div>
            )}
            <button
              onClick={() => {
                if (sendBlocked) return;
                if (v.length >= 20) setShowConfirm(true);
                else doSend();
              }}
              disabled={!okSend || !canAfford || sendBlocked}
              title={sendBlocked ? blockedReason : undefined}
              style={{
                width: '100%',
                padding: 16,
                background: okSend && canAfford && !sendBlocked ? gradG : dis,
                border: 'none',
                borderRadius: 14,
                color: okSend && canAfford && !sendBlocked ? '#fff' : disTxt,
                fontSize: 16,
                fontWeight: 700,
                cursor:
                  okSend && canAfford && !sendBlocked ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow:
                  okSend && canAfford && !sendBlocked
                    ? `0 6px 25px ${C.greenGlow}`
                    : 'none',
              }}
            >
              <I
                d={sendMode === 'schedule' ? ic.cal : ic.send}
                size={20}
                color={okSend && canAfford && !sendBlocked ? '#fff' : disTxt}
              />
              {sendMode === 'schedule' ? 'Schedule Message' : 'Send Message'}
            </button>
          </div>
        )}
        <ConfirmModal />
        <RechargeModal />
      </Shell>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // SCREEN: Home (default)
  // ══════════════════════════════════════════════════════════════════════
  const svc = { ok: true, label: 'Twilio Active' };
  // Every status must land in SOME bucket or the message silently
  // disappears from history. 'sending' (in flight, webhook will flip
  // it) and 'partial_failure' (at least one text went through) ride
  // with Delivered; only fully-failed sends land in the red bucket.
  // The message detail lists exactly which numbers failed.
  const dMsgs = hist.filter(
    (h) =>
      h.status === 'delivered' ||
      h.status === 'sending' ||
      h.status === 'partial_failure'
  );
  const sMsgs = hist.filter((h) => h.status === 'scheduled');
  const fMsgs = hist.filter((h) => h.status === 'failed');
  const cMsgs = hist.filter((h) => h.status === 'cancelled');
  const cats = [
    {
      k: 'delivered',
      l: 'Delivered',
      cnt: dMsgs.length,
      msgs: dMsgs,
      color: C.green,
    },
    {
      k: 'scheduled',
      l: 'Scheduled',
      cnt: sMsgs.length,
      msgs: sMsgs,
      color: C.orange,
    },
    {
      k: 'failed',
      l: 'Failed',
      cnt: fMsgs.length,
      msgs: fMsgs,
      color: C.red,
    },
    {
      k: 'cancelled',
      l: 'Cancelled',
      cnt: cMsgs.length,
      msgs: cMsgs,
      color: C.textMuted,
    },
  ];
  const homeMenuItems = [
    {
      title: 'Analytics',
      icon: <BarChartIcon />,
      onClick: () => setScreen('analytics'),
    },
    {
      title: 'Settings',
      icon: <SettingsIcon />,
      onClick: () => setScreen('settings'),
    },
  ];
  return (
    <Shell pageName="NuVe Boost" menuItems={homeMenuItems}>
      {/* Slim hero strip — just location + Twilio status. The page
          title + icons live in TopToolbar above, so the in-page hero
          no longer needs to repeat them. */}
      <div
        style={{
          background: `linear-gradient(135deg,${C.hStart},${C.hMid},${C.hEnd})`,
          padding: '14px 20px',
          borderRadius: 14,
          marginBottom: 18,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 6px 20px rgba(67,56,202,0.15)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <I d={ic.mega} size={20} color="#fff" />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{LOC}</div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>
              NuVe Boost messaging
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: moduleActive ? '#9ee6b4' : '#ffb4b4',
            }}
          />
          <span style={{ fontSize: 12, opacity: 0.85 }}>
            {moduleActive ? svc.label : 'Paused'}
          </span>
        </div>
      </div>
      <div>
        {/* Credit */}
        <Card
          style={{
            marginBottom: 14,
            padding: 14,
            border: creditEmpty
              ? `2px solid ${C.red}`
              : creditLow
              ? `2px solid ${C.orange}`
              : 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <I
                d={ic.dollar}
                size={15}
                color={creditEmpty ? C.red : creditLow ? C.orange : C.green}
              />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                Credit Balance
              </span>
            </div>
            <span style={{ fontSize: 11, color: C.textMuted }}>
              Limit: ${creditLimit.toFixed(2)}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: creditEmpty ? C.red : creditLow ? C.orange : C.green,
              }}
            >
              ${creditRemaining.toFixed(2)}
            </span>
            <span style={{ fontSize: 12, color: C.textMuted }}>remaining</span>
          </div>
          <div
            style={{
              height: 8,
              background: C.inputBg,
              borderRadius: 4,
              overflow: 'hidden',
              marginBottom: 6,
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${Math.max(creditPct, 0)}%`,
                background: creditEmpty
                  ? C.red
                  : creditLow
                  ? `linear-gradient(90deg,${C.orange},${C.red})`
                  : gradG,
                borderRadius: 4,
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 10, color: C.textMuted }}>
              ${creditUsed.toFixed(2)} used
            </span>
            <span style={{ fontSize: 10, color: C.textMuted }}>
              ${creditLimit.toFixed(2)} limit
            </span>
          </div>
          {locked && (
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  padding: '8px 10px',
                  background: C.redPale,
                  borderRadius: 8,
                  fontSize: 12,
                  color: C.red,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <I d={ic.alert} size={14} color={C.red} />
                Paused by NuVe System — contact support to re-enable.
              </div>
            </div>
          )}
          {!locked && creditLow && !creditEmpty && (
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  padding: '6px 10px',
                  background: C.orangePale,
                  borderRadius: 8,
                  fontSize: 11,
                  color: C.orange,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  marginBottom: 8,
                }}
              >
                <I d={ic.alert} size={12} color={C.orange} />
                Low balance
              </div>
              <RechargeBtn />
            </div>
          )}
          {!locked && creditEmpty && (
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  padding: '8px 10px',
                  background: C.redPale,
                  borderRadius: 8,
                  fontSize: 12,
                  color: C.red,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  marginBottom: 8,
                }}
              >
                <I d={ic.alert} size={14} color={C.red} />
                No credit — messaging paused
              </div>
              <RechargeBtn large />
            </div>
          )}
          {!locked && !creditEmpty && cooldownActive && (
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  padding: '8px 10px',
                  background: C.orangePale,
                  borderRadius: 8,
                  fontSize: 12,
                  color: C.orange,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <I d={ic.alert} size={14} color={C.orange} />
                Cooldown active — you can send again in{' '}
                {Math.floor(cooldownLeft / 60)}:
                {String(cooldownLeft % 60).padStart(2, '0')}.
              </div>
            </div>
          )}
        </Card>
        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <button
            onClick={() => {
              if (sendBlocked) return;
              reset();
              setScreen('compose');
            }}
            style={{
              flex: 1,
              padding: '13px 10px',
              background: sendBlocked ? dis : grad,
              border: 'none',
              borderRadius: 14,
              color: sendBlocked ? disTxt : '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: sendBlocked ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              opacity: sendBlocked ? 0.5 : 1,
              boxShadow: sendBlocked
                ? 'none'
                : '0 6px 20px rgba(91,76,219,0.15)',
            }}
          >
            <I d={ic.plus} size={18} color={sendBlocked ? disTxt : '#fff'} />
            New Message
          </button>
          <button
            onClick={() => {
              if (sendBlocked) return;
              setScreen('quick');
            }}
            style={{
              padding: '13px 16px',
              // Outline button per the spec — white face + border + dark
              // label so it reads on the white container (the old
              // dark-theme '#fff' text was invisible here).
              background: sendBlocked ? C.softBg : C.white,
              border: '1.5px solid #d8dbe8',
              borderRadius: 14,
              color: sendBlocked ? C.softTextDim : C.text,
              fontSize: 14,
              fontWeight: 600,
              cursor: sendBlocked ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              opacity: sendBlocked ? 0.5 : 1,
              boxShadow: sendBlocked ? 'none' : '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <I
              d={ic.zap}
              size={16}
              color={sendBlocked ? C.textDim : C.orange}
            />
            Quick
          </button>
        </div>
        {/* Month spend */}
        <Card style={{ marginBottom: 14, padding: 14 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <I d={ic.dollar} size={16} color={C.accent} />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                This Month
              </span>
            </div>
            <button
              onClick={() => setScreen('analytics')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 12,
                color: C.accent,
                fontWeight: 500,
              }}
            >
              See All →
            </button>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              marginTop: 8,
            }}
          >
            <span style={{ fontSize: 24, fontWeight: 700, color: C.accent }}>
              ${(monthSpend.total + PHONE_FEE_CHARGED).toFixed(2)}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 6,
            }}
          >
            <span style={{ fontSize: 10, color: C.textMuted }}>
              Phone #: ${PHONE_FEE_CHARGED.toFixed(2)}
            </span>
            <span style={{ fontSize: 10, color: C.textMuted }}>
              Messages: ${monthSpend.total.toFixed(2)}
            </span>
            <span style={{ fontSize: 10, color: C.textMuted }}>
              {monthSpend.count} sent
            </span>
          </div>
        </Card>
        {/* Stats */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 10,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              background: C.white,
              borderRadius: 14,
              padding: '12px 8px',
              textAlign: 'center',
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, color: C.accent }}>
              {validCust.length}
            </div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
              Eligible
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setScreen('optout')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setScreen('optout');
            }}
            title="View customers who opted out"
            style={{
              background: C.white,
              borderRadius: 14,
              padding: '12px 8px',
              textAlign: 'center',
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
              cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, color: C.orange }}>
              {optedOutCust.length}
            </div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
              Opt-outs
            </div>
          </div>
          <div
            style={{
              background: C.white,
              borderRadius: 14,
              padding: '12px 8px',
              textAlign: 'center',
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            }}
          >
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: scheduledCount > 0 ? C.orange : C.textDim,
              }}
            >
              {scheduledCount}
            </div>
            <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
              Scheduled
            </div>
          </div>
        </div>
        {(BLACKED.length > 0 || invalidCust.length > 0) && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 14,
            }}
          >
            {BLACKED.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 10px',
                  background: 'rgba(229,68,58,0.1)',
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: C.red,
                  }}
                />
                <span style={{ fontSize: 10, color: C.red, fontWeight: 500 }}>
                  {BLACKED.length} blacklisted
                </span>
              </div>
            )}
            {invalidCust.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 10px',
                  background: C.orangePale,
                  borderRadius: 8,
                }}
              >
                <I d={ic.alert} size={10} color={C.orange} />
                <span
                  style={{ fontSize: 10, color: C.orange, fontWeight: 500 }}
                >
                  {invalidCust.length} invalid phone
                </span>
              </div>
            )}
          </div>
        )}
        <TabBar
          tabs={[
            { k: 'messages', l: 'History', ico: ic.send },
            {
              k: 'templates',
              l: 'Templates',
              ico: ic.bookmark,
              badge: templates.length > 0 ? templates.length : null,
              badgeColor: C.accentLight,
            },
          ]}
          active={homeTab}
          onChange={(v) => {
            if (v === 'templates') setScreen('templates');
            else setHomeTab(v);
          }}
        />
        {cats.map((cat) =>
          cat.cnt === 0 ? null : (
            <div key={cat.k} style={{ marginBottom: 10 }}>
              <button
                onClick={() => setHistFilter((p) => (p === cat.k ? '' : cat.k))}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: histFilter === cat.k ? C.softBgAlt : C.softBg,
                  border: `1px solid ${
                    histFilter === cat.k ? C.borderLight : C.softBg
                  }`,
                  borderRadius: histFilter === cat.k ? '12px 12px 0 0' : '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: cat.color,
                    }}
                  />
                  <span
                    style={{ fontSize: 14, fontWeight: 600, color: C.text }}
                  >
                    {cat.l}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: cat.color,
                      background: `${cat.color}18`,
                      padding: '2px 8px',
                      borderRadius: 10,
                    }}
                  >
                    {cat.cnt}
                  </span>
                </div>
                <div
                  style={{
                    transform:
                      histFilter === cat.k ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                >
                  <I d={ic.chevR} size={16} color={C.textDim} />
                </div>
              </button>
              {histFilter === cat.k && (
                <div
                  style={{
                    background: C.softBg,
                    border: `1px solid ${C.border}`,
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    padding: '6px 6px 2px',
                  }}
                >
                  {cat.msgs.map((m) => (
                    <div
                      key={m.id}
                      onClick={() => {
                        setDetailMsg(m);
                        // Pull the per-recipient delivery rows so the
                        // detail screen can show exactly which phone
                        // numbers failed and why.
                        fetchBoostDetail({ messageId: m.id });
                        setScreen('detail');
                      }}
                      style={{
                        background: C.white,
                        borderRadius: 12,
                        padding: '11px 12px',
                        marginBottom: 6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        boxShadow: '0 1px 8px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          flexShrink: 0,
                          background: m.img
                            ? `linear-gradient(135deg,${C.hStart},${C.hEnd})`
                            : C.accentPale,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <I
                          d={m.img ? ic.image : ic.send}
                          size={14}
                          color={m.img ? '#fff' : C.accent}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.text,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {m.iTitle}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: C.textMuted,
                            marginTop: 2,
                          }}
                        >
                          {m.date} · {m.n} sent · ${(m.cost || 0).toFixed(2)}
                        </div>
                      </div>
                      <I d={ic.chevR} size={14} color={C.textDim} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        )}
      </div>
      <RechargeModal />
    </Shell>
  );
}

// ── Small reusable bits ──
function EmptyHint({ icon, text }) {
  return (
    <div style={{ textAlign: 'center', padding: '30px 20px' }}>
      <I d={icon} size={36} color="#d8dbe8" />
      <p style={{ color: C.softTextMuted, fontSize: 13, marginTop: 10 }}>
        {text}
      </p>
    </div>
  );
}

function AudienceAccordion({
  label,
  badge,
  badgeColor,
  badgeBg,
  iconNode,
  open,
  disabled,
  onToggle,
  children,
}) {
  const active = badge > 0;
  return (
    <div
      style={{
        marginBottom: 8,
        borderRadius: 14,
        border: `1.5px solid ${
          open ? badgeColor || C.accent : active ? badgeBg : C.border
        }`,
        background: C.white,
        boxShadow: open
          ? '0 6px 18px rgba(0,0,0,0.07)'
          : '0 1px 4px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s ease',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          padding: '13px 14px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: active ? badgeBg : C.softBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {iconNode}
        </div>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>
            {label}
          </span>
        </div>
        {badge > 0 && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#fff',
              background: badgeColor,
              padding: '3px 9px',
              borderRadius: 10,
              minWidth: 16,
            }}
          >
            {badge}
          </span>
        )}
        <div
          style={{
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            display: 'flex',
          }}
        >
          <I d={ic.chevR} size={14} color={C.textDim} />
        </div>
      </button>
      {open && (
        <div
          style={{
            padding: '4px 14px 14px',
            borderTop: `1px solid ${C.borderLight}`,
            paddingTop: 12,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function AnalyticsScreen({
  isReport,
  onBack,
  onToReport,
  onToAnalytics,
  hist,
  totalOptOuts,
  LOC,
  isDesktop,
}) {
  // Years + months derived from the actual history. Falls back to the
  // current calendar year when history is empty so the screen renders
  // with a single year tab instead of blanking out.
  const yearsAvailable = useMemo(() => {
    const years = new Set(
      hist
        .filter((h) => h.tsMs > 0)
        .map((h) => String(new Date(h.tsMs).getFullYear()))
    );
    if (years.size === 0) years.add(String(new Date().getFullYear()));
    return [...years].sort().reverse(); // newest year first
  }, [hist]);

  const [aYear, setAYear] = useState(() => yearsAvailable[0]);
  // Re-sync year if the data shifts and the previously-selected year
  // is no longer in the dataset (e.g. user deleted all messages from
  // that year).
  useEffect(() => {
    if (!yearsAvailable.includes(aYear)) setAYear(yearsAvailable[0]);
  }, [yearsAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthsInYear = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    hist
      .filter((h) => h.tsMs > 0)
      .forEach((h) => {
        const d = new Date(h.tsMs);
        if (String(d.getFullYear()) !== aYear) return;
        const monthLabel = ms[d.getMonth()];
        if (!seen.has(monthLabel)) {
          seen.add(monthLabel);
          ordered.push({ label: monthLabel, idx: d.getMonth() });
        }
      });
    ordered.sort((a, b) => a.idx - b.idx);
    return ordered.map((o) => o.label);
  }, [hist, aYear]);

  const [aMonth, setAMonth] = useState(null);
  useEffect(() => {
    if (monthsInYear.length === 0) {
      setAMonth(null);
      return;
    }
    if (!aMonth || !monthsInYear.includes(aMonth)) {
      setAMonth(monthsInYear[monthsInYear.length - 1]); // default to latest month
    }
  }, [monthsInYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const matchesYearMonth = (h, year, monthLabel) => {
    if (h.tsMs <= 0) return false;
    const d = new Date(h.tsMs);
    if (String(d.getFullYear()) !== year) return false;
    if (monthLabel && ms[d.getMonth()] !== monthLabel) return false;
    return true;
  };

  // Same money rule as the home "This Month" card: only messages that
  // actually consumed credit count as spend. Scheduled = not charged
  // yet; cancelled = never charged; failed = auto-refunded.
  const ANALYTICS_SPEND_STATUSES = ['sending', 'delivered', 'partial_failure'];
  const getMonthData = (monthLabel) => {
    let t = 0;
    let c = 0;
    let r = 0;
    let d = 0;
    let f = 0;
    let o = 0;
    hist.forEach((h) => {
      if (
        ANALYTICS_SPEND_STATUSES.includes(h.status) &&
        matchesYearMonth(h, aYear, monthLabel)
      ) {
        t += h.cost || 0;
        c++;
        r += h.n || 0;
        d += h.delivered || 0;
        f += h.failed || 0;
        o += h.optOuts || 0;
      }
    });
    const a = d + f;
    return {
      total: t,
      count: c,
      recipients: r,
      rate: a > 0 ? Math.round((d / a) * 100) : 0,
      opts: o,
      month: monthLabel,
    };
  };
  const md = monthsInYear.map((m) => ({
    ...getMonthData(m),
    label: `${m} ${aYear}`,
  }));
  const cur = md.find((m) => m.month === aMonth) ||
    md[md.length - 1] || {
      total: 0,
      count: 0,
      recipients: 0,
      rate: 0,
      opts: 0,
      month: '',
    };
  const mxS = Math.max(...md.map((m) => m.total), 0.01);
  const aD = hist.reduce((s, h) => s + (h.delivered || 0), 0);
  const aF = hist.reduce((s, h) => s + (h.failed || 0), 0);
  const aT = aD + aF;
  const aR = aT > 0 ? Math.round((aD / aT) * 100) : 0;

  if (isReport) {
    const today = new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const rt = md.reduce(
      (a, m) => ({
        t: a.t + m.total,
        c: a.c + m.count,
        r: a.r + m.recipients,
        o: a.o + m.opts,
      }),
      { t: 0, c: 0, r: 0, o: 0 }
    );
    // Message-detail rows for the selected report year, newest first.
    const repMsgs = hist
      .filter(
        (h) =>
          h.status !== 'cancelled' &&
          h.tsMs > 0 &&
          String(new Date(h.tsMs).getFullYear()) === aYear
      )
      .sort((a, b) => b.tsMs - a.tsMs);
    return (
      <Shell
        pageName="Spending Report"
        onBack={onToAnalytics}
        menuItems={[
          {
            title: 'Print',
            icon: <FileDownloadIcon />,
            onClick: () => window.print(),
          },
        ]}
      >
        <div
          style={{
            background: '#fff',
            minHeight: '100%',
            padding: '20px 16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              borderBottom: `3px solid ${C.accent}`,
              paddingBottom: 14,
              marginBottom: 16,
            }}
          >
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>
                NuVe Boost
              </div>
              <div style={{ fontSize: 11, color: C.textMuted }}>
                Spending Report
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {LOC}
              </div>
              <div style={{ fontSize: 11, color: C.textMuted }}>{aYear}</div>
              <div style={{ fontSize: 10, color: C.textMuted }}>{today}</div>
            </div>
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: C.textSec,
              borderBottom: `2px solid ${C.accent}`,
              paddingBottom: 5,
              marginBottom: 10,
            }}
          >
            Monthly Summary
          </div>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginBottom: 16,
              fontSize: 12,
            }}
          >
            <thead>
              <tr style={{ background: C.inputBg }}>
                {['Month', 'Spent', 'Msgs', 'Recip.', 'Dlvry', 'Opt-out'].map(
                  (h, i) => (
                    <th
                      key={h}
                      style={{
                        padding: '8px 6px',
                        textAlign:
                          i === 1 ? 'right' : i === 0 ? 'left' : 'center',
                        fontSize: 10,
                        fontWeight: 600,
                        color: C.textMuted,
                        textTransform: 'uppercase',
                      }}
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {md.map((m) => (
                <tr
                  key={m.month}
                  style={{ borderBottom: `1px solid ${C.borderLight}` }}
                >
                  <td
                    style={{
                      padding: '7px 6px',
                      fontWeight: 500,
                      color: C.text,
                    }}
                  >
                    {m.month} {aYear}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'right',
                      fontWeight: 600,
                      color: C.accent,
                    }}
                  >
                    ${m.total.toFixed(2)}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'center',
                      color: C.textSec,
                    }}
                  >
                    {m.count}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'center',
                      color: C.textSec,
                    }}
                  >
                    {m.recipients}
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'center',
                      color: m.rate >= 90 ? C.green : C.orange,
                    }}
                  >
                    {m.rate}%
                  </td>
                  <td
                    style={{
                      padding: '7px 6px',
                      textAlign: 'center',
                      color: m.opts > 0 ? C.orange : C.textDim,
                    }}
                  >
                    {m.opts}
                  </td>
                </tr>
              ))}
              <tr style={{ background: C.accentPale, fontWeight: 700 }}>
                <td style={{ padding: '8px 6px', color: C.text }}>Total</td>
                <td
                  style={{
                    padding: '8px 6px',
                    textAlign: 'right',
                    color: C.accent,
                  }}
                >
                  ${rt.t.toFixed(2)}
                </td>
                <td
                  style={{
                    padding: '8px 6px',
                    textAlign: 'center',
                    color: C.text,
                  }}
                >
                  {rt.c}
                </td>
                <td
                  style={{
                    padding: '8px 6px',
                    textAlign: 'center',
                    color: C.text,
                  }}
                >
                  {rt.r}
                </td>
                <td
                  style={{
                    padding: '8px 6px',
                    textAlign: 'center',
                    color: C.textMuted,
                  }}
                >
                  —
                </td>
                <td
                  style={{
                    padding: '8px 6px',
                    textAlign: 'center',
                    color: C.text,
                  }}
                >
                  {rt.o}
                </td>
              </tr>
            </tbody>
          </table>
          {/* Message Detail — per-message breakdown for the selected year */}
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: C.textSec,
              borderBottom: `2px solid ${C.accent}`,
              paddingBottom: 5,
              marginBottom: 10,
            }}
          >
            Message Detail
          </div>
          {repMsgs.length === 0 ? (
            <div
              style={{
                padding: '14px 0',
                textAlign: 'center',
                fontSize: 11,
                color: C.textDim,
              }}
            >
              No messages in {aYear}.
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginBottom: 16,
                fontSize: 11,
              }}
            >
              <thead>
                <tr style={{ background: C.inputBg }}>
                  {['Title', 'Date', 'Sent', 'Cost'].map((h, i) => (
                    <th
                      key={h}
                      style={{
                        padding: '7px 6px',
                        textAlign:
                          i === 3 ? 'right' : i === 2 ? 'center' : 'left',
                        fontSize: 10,
                        fontWeight: 600,
                        color: C.textMuted,
                        textTransform: 'uppercase',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {repMsgs.map((h) => (
                  <tr
                    key={h.id}
                    style={{ borderBottom: `1px solid ${C.borderLight}` }}
                  >
                    <td
                      style={{
                        padding: 6,
                        color: C.text,
                        maxWidth: 120,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {h.iTitle}
                    </td>
                    <td
                      style={{
                        padding: 6,
                        color: C.textMuted,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h.date}
                    </td>
                    <td
                      style={{
                        padding: 6,
                        textAlign: 'center',
                        color: C.textSec,
                      }}
                    >
                      {h.delivered}/{h.n}
                    </td>
                    <td
                      style={{
                        padding: 6,
                        textAlign: 'right',
                        fontWeight: 600,
                        color: C.accent,
                      }}
                    >
                      ${(h.cost || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div
            style={{
              borderTop: `1px solid ${C.border}`,
              paddingTop: 10,
              textAlign: 'center',
              fontSize: 10,
              color: C.textDim,
            }}
          >
            NuVe Boost — Powered by NuVe System
          </div>
          <button
            onClick={onToAnalytics}
            style={{
              width: '100%',
              padding: 12,
              background: grad,
              border: 'none',
              borderRadius: 10,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              marginTop: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <I d={ic.chevL} size={14} color="#fff" /> Back to Analytics
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      pageName="Analytics"
      onBack={onBack}
      menuItems={[
        {
          title: 'Open printable report',
          icon: <FileDownloadIcon />,
          onClick: onToReport,
        },
      ]}
    >
      <div>
        {/* Year selector — derived from real data, not hardcoded. Shows
            one button per year that has at least one message. Falls
            back to the current calendar year when history is empty. */}
        {yearsAvailable.length > 1 ? (
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {yearsAvailable.map((y) => (
              <button
                key={y}
                onClick={() => setAYear(y)}
                style={{
                  flex: 1,
                  padding: '10px 6px',
                  background: aYear === y ? grad : C.softBg,
                  border: `1.5px solid ${
                    aYear === y ? C.softTextDim : C.borderLight
                  }`,
                  borderRadius: 10,
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: aYear === y ? '#fff' : C.softTextMuted,
                  }}
                >
                  {y}
                </div>
              </button>
            ))}
          </div>
        ) : (
          // Single-year case — show the year as a pill so the screen
          // doesn't look orphaned without a selector row.
          <div
            style={{
              marginBottom: 14,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                padding: '6px 14px',
                background: C.softBg,
                border: '1.5px solid #eef0f6',
                borderRadius: 20,
                fontSize: 13,
                fontWeight: 700,
                color: C.textSec,
              }}
            >
              {aYear}
            </div>
          </div>
        )}

        {/* Month tabs — filtered by selected year. Natural width chips
            (NOT flex:1 — a single month would stretch across the whole
            row) in a horizontally scrollable rail so 12 months never
            squeeze or wrap. */}
        {monthsInYear.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginBottom: 16,
              overflowX: 'auto',
              paddingBottom: 2,
            }}
          >
            {monthsInYear.map((m) => {
              const d = md.find((x) => x.month === m) || { total: 0 };
              const sel = aMonth === m;
              return (
                <button
                  key={m}
                  onClick={() => setAMonth(m)}
                  style={{
                    flex: '0 0 auto',
                    minWidth: 64,
                    padding: '9px 14px',
                    background: sel ? C.accentPale : C.white,
                    border: `1.5px solid ${sel ? C.accent : C.border}`,
                    borderRadius: 10,
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.12s ease',
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: sel ? C.accent : C.textSec,
                    }}
                  >
                    {m}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: sel ? C.accent : C.textDim,
                      marginTop: 2,
                    }}
                  >
                    ${d.total.toFixed(2)}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <Card style={{ marginBottom: 14 }}>
          <CLbl>
            {cur.month} {aYear}
          </CLbl>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
          >
            <div
              style={{
                textAlign: 'center',
                padding: 12,
                background: C.accentPale,
                borderRadius: 12,
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 700, color: C.accent }}>
                ${cur.total.toFixed(2)}
              </div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                Total Spent
              </div>
            </div>
            <div
              style={{
                textAlign: 'center',
                padding: 12,
                background: C.greenPale,
                borderRadius: 12,
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 700, color: C.green }}>
                {cur.count}
              </div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                Messages Sent
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 10,
              marginTop: 12,
            }}
          >
            <div
              style={{
                textAlign: 'center',
                padding: 10,
                background: C.inputBg,
                borderRadius: 10,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>
                {cur.recipients}
              </div>
              <div style={{ fontSize: 9, color: C.textMuted, marginTop: 2 }}>
                Recipients
              </div>
            </div>
            <div
              style={{
                textAlign: 'center',
                padding: 10,
                background: C.inputBg,
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color:
                    cur.rate >= 90
                      ? C.green
                      : cur.rate >= 70
                      ? C.orange
                      : C.red,
                }}
              >
                {cur.rate}%
              </div>
              <div style={{ fontSize: 9, color: C.textMuted, marginTop: 2 }}>
                Delivery
              </div>
            </div>
            <div
              style={{
                textAlign: 'center',
                padding: 10,
                background: C.inputBg,
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: (cur.opts || 0) > 0 ? C.orange : C.textDim,
                }}
              >
                {cur.opts || 0}
              </div>
              <div style={{ fontSize: 9, color: C.textMuted, marginTop: 2 }}>
                Opt-outs
              </div>
            </div>
          </div>
        </Card>
        <Card style={{ marginBottom: 14 }}>
          <CLbl>Spending — {aYear}</CLbl>
          {md.map((m) => {
            const pct = mxS > 0 ? (m.total / mxS) * 100 : 0;
            return (
              <div key={m.month} style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: m.month === aMonth ? C.text : C.textMuted,
                    }}
                  >
                    {m.label}
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: m.month === aMonth ? C.accent : C.textMuted,
                    }}
                  >
                    ${m.total.toFixed(2)}
                  </span>
                </div>
                <div
                  style={{
                    height: 8,
                    background: C.inputBg,
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${pct}%`,
                      background:
                        m.month === aMonth
                          ? `linear-gradient(90deg,${C.hStart},${C.accentLight})`
                          : C.border,
                      borderRadius: 4,
                      transition: 'width 0.5s ease',
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 3,
                  }}
                >
                  <span style={{ fontSize: 10, color: C.textDim }}>
                    {m.count} msgs
                  </span>
                  <span style={{ fontSize: 10, color: C.textDim }}>
                    {m.recipients} recipients
                  </span>
                </div>
              </div>
            );
          })}
        </Card>
        <Card style={{ marginBottom: 14 }}>
          <CLbl>All Time</CLbl>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 10,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.green }}>
                {aR}%
              </div>
              <div style={{ fontSize: 10, color: C.textMuted }}>
                Delivery Rate
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.red }}>
                {totalOptOuts}
              </div>
              <div style={{ fontSize: 10, color: C.textMuted }}>Opt-outs</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.accent }}>
                {hist.filter((h) => h.status !== 'cancelled').length}
              </div>
              <div style={{ fontSize: 10, color: C.textMuted }}>Total Sent</div>
            </div>
          </div>
        </Card>
        <button
          onClick={onToReport}
          style={{
            width: '100%',
            padding: 13,
            background: grad,
            border: 'none',
            borderRadius: 12,
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <I d={ic.upload} size={16} color="#fff" />
          Download / Print Report
        </button>
      </div>
    </Shell>
  );
}

// Tiny helper for the home cards — match-bucket label for history rows.
function audienceLabelFromCount(m) {
  if (m.audienceType === 'all') return 'All';
  if (m.audienceType === 'single') return 'Single';
  return 'Mixed';
}

// ── Redux glue ──
const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  customers: state.boost?.customers || [],
  messages: state.boost?.messages || [],
  templates: state.boost?.templates || [],
  credit: state.boost?.credit || null,
  settings: state.boost?.settings || null,
  platformConfig: state.boost?.platformConfig || null,
  sending: state.boost?.sending || false,
  sendResult: state.boost?.sendResult || null,
  detail: state.boost?.detail || null,
});

const actionCreators = {
  fetchBoostHistory,
  fetchBoostCustomers,
  fetchBoostTemplates,
  fetchBoostCredit,
  fetchBoostSettings,
  fetchBoostAnalytics,
  fetchBoostPlatformConfig,
  fetchBoostDetail,
  sendBoostBulk,
  retryBoostMessage,
  retryBoostRecipient,
  cancelBoostMessage,
  saveBoostTemplate,
  deleteBoostTemplate,
  saveBoostSettings,
  saveBoostAdmin,
  resetBoostBalance,
  pauseBoost,
};

export default withShipment({ mapStateToProps, actionCreators }, NuVeBoost);
